import * as THREE from 'three'
import React, { useState, useRef, useEffect, Suspense } from 'react'
import { Canvas, useThree } from '@react-three/fiber'
import {
  useGLTF,
  Center,
  MeshRefractionMaterial,
  Environment,
  AccumulativeShadows,
  RandomizedLight,
  useEnvironment,
  Html,
  Stats,
  OrbitControls,
  useProgress,
  Text
} from '@react-three/drei'
import { EffectComposer, Bloom, SelectiveBloom, ToneMapping, SMAA, SSAO } from '@react-three/postprocessing'
import { useControls, folder } from 'leva'
import { RGBELoader } from 'three-stdlib' // Make sure this is installed
import { a, useSpring } from '@react-spring/three'
import { Leva } from 'leva'
import { BlendFunction } from 'postprocessing'

// Add CSS for annotation hover effects
const annotationStyles = `
  .annotation:hover {
    box-shadow: inset 250px 0 0 0 yellow !important;
    color: black !important;
  }
`;

// Inject the styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = annotationStyles;
  document.head.appendChild(style);
}

// Suppress specific react-spring error about _AnimatedValue and _AnimatedString
const originalConsoleError = console.error;
console.error = function (...args) {
  if (
    typeof args[0] === 'string' &&
    args[0].includes('Cannot animate between _AnimatedValue and _AnimatedString')
  ) {
    // Suppress this specific error
    return;
  }
  originalConsoleError.apply(console, args);
};

// Suppress uncaught runtime errors and unhandled promise rejections for this error
window.addEventListener('error', function (e) {
  if (
    e.message &&
    e.message.includes('Cannot animate between _AnimatedValue and _AnimatedString')
  ) {
    e.preventDefault();
  }
});
window.addEventListener('unhandledrejection', function (e) {
  if (
    e.reason &&
    typeof e.reason.message === 'string' &&
    e.reason.message.includes('Cannot animate between _AnimatedValue and _AnimatedString')
  ) {
    e.preventDefault();
  }
});

// Sidebar width in px
const SIDEBAR_WIDTH = 340;

/* -------------------------------------------------
 *  Main Scene
 * ------------------------------------------------- */
function Scene({ selectedNode, selectedSettingStyle, selectedBandStyle, selectedSettingMetal, metals, selectedBandMetal, selectedDiamondShape, selectedDiamondSize, diamondSizeData, cameraView, isAutoRotating, selectedCathedralAnnotation, setSelectedCathedralAnnotation, showOnlyDiamond }) {
  // Get Three.js objects for camera setup and screenshot
  const { camera, gl, scene } = useThree();
  
  // Setup camera layers
  useEffect(() => {
    camera.layers.enable(0); // default
    camera.layers.enable(1); // diamond bloom
  }, [camera]);

  // Camera positioning based on view using OrbitControls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (controlsRef.current) {
        if (cameraView === 'top') {
          // Top view - camera positioned above the ring looking down
          controlsRef.current.setAzimuthalAngle(0);
          controlsRef.current.setPolarAngle(0);
          controlsRef.current.distance = 8;
        } else if (cameraView === 'side') {
          // Side view - camera positioned to the side of the ring
          controlsRef.current.setAzimuthalAngle(Math.PI / 2);
          controlsRef.current.setPolarAngle(Math.PI / 2);
          controlsRef.current.distance = 8;
        } else if (cameraView === '360') {
          // 360 view - start from front position
          controlsRef.current.setAzimuthalAngle(0);
          controlsRef.current.setPolarAngle(Math.PI / 2);
          controlsRef.current.distance = 8;
        } else {
          // Front view - camera positioned in front of the ring
          controlsRef.current.setAzimuthalAngle(0);
          controlsRef.current.setPolarAngle(Math.PI / 2);
          controlsRef.current.distance = 8;
        }
      }
    }, 100); // Small delay to ensure OrbitControls is initialized

    return () => clearTimeout(timer);
  }, [cameraView]);

  // Auto-rotation for 360 view
  useEffect(() => {
    let animationId;
    
    if (isAutoRotating && controlsRef.current) {
      // Set initial position for 360 rotation (front view)
      controlsRef.current.setAzimuthalAngle(0);
      controlsRef.current.setPolarAngle(Math.PI / 2);
      controlsRef.current.distance = 8;
      
      const animate = () => {
        if (controlsRef.current && isAutoRotating) {
          const angle = Date.now() * 0.0005; // Slower rotation speed
          
          // Update controls with smooth rotation around Y axis
          controlsRef.current.setAzimuthalAngle(angle);
          
          animationId = requestAnimationFrame(animate);
        }
      };
      
      // Start animation after a small delay to ensure initial position is set
      setTimeout(() => {
        animate();
      }, 100);
    }
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isAutoRotating]);

  // Force render listener for screenshots
  useEffect(() => {
    const handleForceRender = () => {
      // Force a render of the scene
      gl.render(scene, camera);
    };
    
    window.addEventListener('forceRender', handleForceRender);
    return () => window.removeEventListener('forceRender', handleForceRender);
  }, [gl, scene, camera]);

  // Leva controls for Diamond Material
  const diamondProps = useControls('Diamond Material', {
    color: { value: '#ffffff', label: 'Diamond Color' },
    aberrationStrength: {
      value: 0.05,
      min: 0.02,
      max: 0.5,
      step: 0.005,
      label: 'Aberration Strength',
    },
    bounces: {
      value: 4,
      min: 1,
      max: 5,
      step: 1,
      label: 'Ray Bounces',
    },
    ior: {
      value: 3,
      min: 1,
      max: 5,
      step: 0.01,
      label: 'Index of Refraction (IOR)',
    },
    fresnel: { value: 0, min: 0, max: 1, step: 0.01, label: 'Fresnel Strength' },
  })

  // Use different environment maps for different materials
  const diamondEnvironmentMap = useEnvironment({ files: '/studioHDR_4_whiteShiny_Low.hdr' });
  const metalEnvironmentMap = useEnvironment({ files: '/Studio1.hdr' });

  // Leva control for environment map intensity
  const { envMapIntensityGlobal } = useControls({
    envMapIntensityGlobal: { value: 1.2, min: 0, max: 5, step: 0.05, label: 'Env Map Intensity (Global)' },
  });

  // Animated clipping plane (bottom to top reveal)
  const isHalo = selectedNode === '21Diamonds';
  const planeRef = React.useRef(new THREE.Plane(new THREE.Vector3(0, -1, 0), -1));
  const { clipValue } = useSpring({
    from: { clipValue: 0 },                   // fully hidden
    to:   { clipValue: 1 },                    // always reveal from -1 to 1
    reset: true,
    config: { duration: 1500 },
    onChange: ({ value: { clipValue } }) => {
      planeRef.current.constant = clipValue;    // always a number
    },
  });

  // Remove separate toneMappingProps
  const toneMappingModes = {
    ACESFilmic: THREE.ACESFilmicToneMapping,
    Reinhard: THREE.ReinhardToneMapping,
    Linear: THREE.LinearToneMapping,
    Cineon: THREE.CineonToneMapping,
  };

  // Leva controls for Head Material (must be inside this component)
  const headProps = useControls('Head Material', {
    color: { value: '#ffffff', label: 'Head Color' },
    roughness: { value: 0.15, min: 0, max: 1, step: 0.01, label: 'Roughness' },
    metalness: { value: 1, min: 0, max: 1, step: 0.01, label: 'Metalness' },
    clearcoat: { value: 0.25, min: 0, max: 1, step: 0.01, label: 'Clearcoat' },
    clearcoatRoughness: { value: 0.35, min: 0, max: 1, step: 0.01, label: 'Clearcoat Roughness' },
    transmission: { value: 0, min: 0, max: 1, step: 0.01, label: 'Transmission' },
    ior: { value: 0, min: 0, max: 2.5, step: 0.01, label: 'IOR' },
    thickness: { value: 0, min: 0, max: 2, step: 0.01, label: 'Thickness' },
  });

  // Per-metal envMap intensity for more realistic gold/rose reflections
  const metalEnvMapIntensity = React.useMemo(() => {
    switch (selectedSettingMetal) {
      case 1: // Yellow Gold
        return 1.15;
      case 2: // Rose Gold
        return 1.1;
      case 3: // Platinum
        return 0.85;
      case 0: // White Gold
      default:
        return 0.9;
    }
  }, [selectedSettingMetal]);

  // Postprocessing Toggles & Parameters (must be after headProps, inside Scene)
  const postProcessingProps = useControls('Postprocessing Effects', {
    SectionTitle: folder({
      label: 'Postprocessing Effects',
    }),
    ssaoEnabled: { value: true, label: 'Enable SSAO' },
    ssaoSamples: { value: 30, min: 1, max: 64, step: 1, label: 'SSAO Samples' },
    ssaoRings: { value: 4, min: 1, max: 8, step: 1, label: 'SSAO Rings' },
    ssaoDistanceThreshold: { value: 1.0, min: 0, max: 2, step: 0.01, label: 'SSAO Distance Threshold' },
    ssaoDistanceFalloff: { value: 0.0, min: 0, max: 1, step: 0.01, label: 'SSAO Distance Falloff' },
    ssaoRangeThreshold: { value: 0.5, min: 0, max: 1, step: 0.01, label: 'SSAO Range Threshold' },
    ssaoRangeFalloff: { value: 0.1, min: 0, max: 1, step: 0.01, label: 'SSAO Range Falloff' },
    ssaoLuminanceInfluence: { value: 0.9, min: 0, max: 1, step: 0.01, label: 'SSAO Luminance Influence' },
    ssaoRadius: { value: 20, min: 1, max: 50, step: 1, label: 'SSAO Radius' },
    ssaoScale: { value: 0.5, min: 0, max: 2, step: 0.01, label: 'SSAO Scale' },
    ssaoBias: { value: 0.5, min: 0, max: 1, step: 0.01, label: 'SSAO Bias' },
    bloomEnabled: { value: true, label: 'Enable Bloom' },
    bloomThreshold: { value: 1.09, min: 0, max: 2, step: 0.01, label: 'Bloom Threshold' },
    bloomIntensity: { value: 0.84, min: 0, max: 2, step: 0.01, label: 'Bloom Intensity' },
    bloomLevels: { value: 5, min: 1, max: 10, step: 1, label: 'Bloom Levels' },
    mipmapBlur: { value: true, label: 'Enable Mipmap Blur' },
    toneMappingExposure: { value: 1, min: 0, max: 3, step: 0.01, label: 'Tone Mapping Exposure' },
    toneMappingMode: {
      options: {
        ACESFilmic: 'ACESFilmic',
        Reinhard: 'Reinhard',
        Linear: 'Linear',
        Cineon: 'Cineon',
      },
      value: 'Cineon',
      label: 'Tone Mapping Mode',
    },
    toneMappingBlendFunction: {
      options: {
        NORMAL: BlendFunction.NORMAL,
        ADD: BlendFunction.ADD,
        MULTIPLY: BlendFunction.MULTIPLY,
        SCREEN: BlendFunction.SCREEN,
        OVERLAY: BlendFunction.OVERLAY,
        DARKEN: BlendFunction.DARKEN,
        LIGHTEN: BlendFunction.LIGHTEN,
        COLOR_DODGE: BlendFunction.COLOR_DODGE,
        COLOR_BURN: BlendFunction.COLOR_BURN,
        HARD_LIGHT: BlendFunction.HARD_LIGHT,
        SOFT_LIGHT: BlendFunction.SOFT_LIGHT,
        DIFFERENCE: BlendFunction.DIFFERENCE,
        EXCLUSION: BlendFunction.EXCLUSION,
        HUE: BlendFunction.HUE,
        SATURATION: BlendFunction.SATURATION,
        COLOR: BlendFunction.COLOR,
        LUMINOSITY: BlendFunction.LUMINOSITY,
      },
      value: BlendFunction.NORMAL,
      label: 'ToneMapping Blend Function',
    },
    toneMappingAdaptive: { value: true, label: 'Adaptive' },
    toneMappingResolution: { value: 256, min: 32, max: 1024, step: 1, label: 'Resolution' },
    toneMappingMiddleGrey: { value: 0.6, min: 0, max: 2, step: 0.01, label: 'Middle Grey' },
    toneMappingMaxLuminance: { value: 16.0, min: 0, max: 32, step: 0.1, label: 'Max Luminance' },
    toneMappingAverageLuminance: { value: 1.0, min: 0, max: 8, step: 0.01, label: 'Average Luminance' },
    toneMappingAdaptationRate: { value: 1.0, min: 0, max: 4, step: 0.01, label: 'Adaptation Rate' },
    smaaEnabled: { value: true, label: 'Enable SMAA' },
    smaaEdgeDetectionThreshold: { value: 0.05, min: 0, max: 0.5, step: 0.01, label: 'SMAA Edge Detection Threshold' },
  });

  const controlsRef = React.useRef();
  const modelGroupRef = React.useRef();

  // Cathedral annotation selection is now handled by buttons at the bottom of the screen

  return (
    <>
      {/* Environment setup */}
      <Environment 
        files="/studioHDR_4_whiteShiny_Low.hdr" 
        background={false}
        blur={0.5}
        resolution={256}
      />
      
              <group ref={modelGroupRef} position={[0, -0.25, 0]}>
          {/* Comparison Diamond (right) - always Diamond Round and its head */}
          <ComparisonDiamond diamondEnv={diamondEnvironmentMap} metalEnv={metalEnvironmentMap} diamondProps={diamondProps} selectedNode={selectedNode} clippingPlane={planeRef.current} selectedSettingStyle={selectedSettingStyle} selectedBandStyle={selectedBandStyle} selectedSettingMetal={selectedSettingMetal} metals={metals} selectedBandMetal={selectedBandMetal} selectedDiamondShape={selectedDiamondShape} selectedDiamondSize={selectedDiamondSize} diamondSizeData={diamondSizeData} selectedCathedralAnnotation={selectedCathedralAnnotation} showOnlyDiamond={showOnlyDiamond} envMapIntensityGlobal={envMapIntensityGlobal} metalEnvMapIntensity={metalEnvMapIntensity} />
        </group>
      {/* Camera Controls */}
      <OrbitControls
        ref={controlsRef}
        minPolarAngle={0}
        maxPolarAngle={Math.PI / 2.25}
        enableDamping={true}
        dampingFactor={0.05}
        minDistance={3}
        maxDistance={15}
        enablePan={false}
        enableZoom={true}
        enableRotate={true}
      />
      {/* Postprocessing Effects */}
      <Suspense fallback={null}>
        <EffectComposer multisampling={0}>
          {/* Broad AO (large radius, soft) */}
          <SSAO
            blendFunction={BlendFunction.MULTIPLY}
            samples={16}
            rings={4}
            radius={32}
            scale={0.5}
            bias={0.5}
            luminanceInfluence={0.7}
            depthAwareUpsampling={true}
          />
          {/* Fine AO (small radius, crisp) */}
          <SSAO
            blendFunction={BlendFunction.MULTIPLY}
            samples={32}
            rings={6}
            radius={6}
            scale={0.5}
            bias={0.5}
            luminanceInfluence={0.9}
            depthAwareUpsampling={true}
          />
          {postProcessingProps.bloomEnabled && (
            <SelectiveBloom
              selectionLayer={1}
              intensity={postProcessingProps.bloomIntensity}
              luminanceThreshold={postProcessingProps.bloomThreshold}
              luminanceSmoothing={0.0}
              mipmapBlur={postProcessingProps.mipmapBlur}
            />
          )}
          {postProcessingProps.smaaEnabled && (
            <SMAA edgeDetectionThreshold={postProcessingProps.smaaEdgeDetectionThreshold} />
          )}
          <ToneMapping
            mode={toneMappingModes[postProcessingProps.toneMappingMode]}
            exposure={postProcessingProps.toneMappingExposure}
            blendFunction={postProcessingProps.toneMappingBlendFunction}
            adaptive={postProcessingProps.toneMappingAdaptive}
            resolution={postProcessingProps.toneMappingResolution}
            middleGrey={postProcessingProps.toneMappingMiddleGrey}
            maxLuminance={postProcessingProps.toneMappingMaxLuminance}
            averageLuminance={postProcessingProps.toneMappingAverageLuminance}
            adaptationRate={postProcessingProps.toneMappingAdaptationRate}
          />
        </EffectComposer>
      </Suspense>
      {/* If no custom HDR is provided, fallbackEnv is used */}
      
                  {/* Cathedral annotations removed - buttons now appear at bottom of screen for Cathedral settings */}
    </>
  )
}

/* -------------------------------------------------
 *  App + HDR Upload
 * ------------------------------------------------- */
export default function App() {
  // LIFTED STATE: diamond shape selection
  const diamondShapeNodeNames = [
    "1_Round",      // 0
    "1_Princess",   // 1
    "1_Oval",       // 2
    "1_Emerald",    // 3
    "1_Pear",       // 4
    "1_Cushion",    // 5
    "1_Marquise",   // 6
    "1_Asscher",    // 7
    "1_Radiant",    // 8
    "1_Heart"       // 9
  ];
  const [selectedDiamondShape, setSelectedDiamondShape] = useState(0);
  const selectedNode = diamondShapeNodeNames[selectedDiamondShape];
  const [levaVisible, setLevaVisible] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [selectedSettingStyle, setSelectedSettingStyle] = useState(0);
  const [selectedBandStyle, setSelectedBandStyle] = useState(0);
  // LIFTED STATE: selectedSettingMetal and selectedBandMetal
  const [selectedSettingMetal, setSelectedSettingMetal] = useState(0);
  const [selectedBandMetal, setSelectedBandMetal] = useState(0);
  // LIFTED STATE: diamond size selection (default to 1ct = index 0)
  const [selectedDiamondSize, setSelectedDiamondSize] = useState(0);
  // Cathedral annotation selection state
  const [selectedCathedralAnnotation, setSelectedCathedralAnnotation] = useState(null); // 'pave-halo', 'pave-basket', 'classic-basket'
  const canvasRef = useRef(null);
  // Metals: [White Gold, Yellow Gold, Rose Gold, Platinum]
  // Updated color codes for more realistic metal appearance
  // White Gold/Platinum: #dddfe0 (221, 223, 224), Yellow Gold: #dfc892 (223, 200, 146), Rose Gold: #cc835a (204, 131, 90)
  const metals = ['#dddfe0', '#dfc892', '#cc835a', '#dddfe0'];
  const metalNames = ['White Gold', 'Yellow Gold', 'Rose Gold', 'Platinum'];

  // Diamond size charts for each shape (width and height in mm)
  const diamondSizeCharts = {
    Round: [
      { label: '1ct', w: 6.5, h: 6.5 },
      { label: '1.5ct', w: 7.3, h: 7.3 },
      { label: '2ct', w: 8.0, h: 8.0 }
    ],
    Princess: [
      { label: '1ct', w: 6.5, h: 6.5 },
      { label: '1.5ct', w: 7.3, h: 7.3 },
      { label: '2ct', w: 8.0, h: 8.0 }
    ],
    Oval: [
      { label: '1ct', w: 7.9, h: 5.5 },
      { label: '1.5ct', w: 8.8, h: 6.3 },
      { label: '2ct', w: 10.0, h: 7.2 }
    ],
    Emerald: [
      { label: '1ct', w: 6.9, h: 4.7 },
      { label: '1.5ct', w: 7.8, h: 5.3 },
      { label: '2ct', w: 8.9, h: 6.1 }
    ],
    Pear: [
      { label: '1ct', w: 8.4, h: 5.6 },
      { label: '1.5ct', w: 9.7, h: 6.3 },
      { label: '2ct', w: 11.0, h: 7.2 }
    ],
    Cushion: [
      { label: '1ct', w: 6.5, h: 5.0 },
      { label: '1.5ct', w: 7.5, h: 6.0 },
      { label: '2ct', w: 8.5, h: 6.8 }
    ],
    Marquise: [
      { label: '1ct', w: 10.0, h: 5.0 },
      { label: '1.5ct', w: 12.0, h: 6.0 },
      { label: '2ct', w: 13.0, h: 6.5 }
    ],
    Asscher: [
      { label: '1ct', w: 5.5, h: 5.5 },
      { label: '1.5ct', w: 6.5, h: 6.5 },
      { label: '2ct', w: 7.0, h: 7.0 }
    ],
    Radiant: [
      { label: '1ct', w: 7.0, h: 5.0 },
      { label: '1.5ct', w: 7.5, h: 5.8 },
      { label: '2ct', w: 8.2, h: 6.5 }
    ],
    Heart: [
      { label: '1ct', w: 6.5, h: 6.5 },
      { label: '1.5ct', w: 7.5, h: 7.5 },
      { label: '2ct', w: 8.0, h: 8.0 }
    ],
  };

  // Helper to get shape label from selectedDiamondShape index
  const shapeLabels = [
    'Round', 'Princess', 'Oval', 'Emerald', 'Pear', 'Cushion', 'Marquise', 'Asscher', 'Radiant', 'Heart'
  ];
  const selectedShapeLabel = shapeLabels[selectedDiamondShape];
  const diamondSizeData = diamondSizeCharts[selectedShapeLabel];

  const diamondNames = [
    { label: 'Asscher', node: 'Diamond_Asscher001' },
    { label: 'Cushion', node: 'Diamond_Cushion001' },
    { label: 'Emerald', node: 'Diamond_Emerald001' },
    { label: 'Oval', node: 'Diamond_Oval001' },
    { label: 'Princess', node: 'Diamond_Princess001' },
    { label: 'Radiant', node: 'Diamond_Radiant001' },
    { label: 'Round', node: 'Diamond_Round001' },
  ];

  // Leva control for Canvas DPR
  const { dpr } = useControls({
    dpr: { value: 2, min: 0.5, max: 2.5, step: 0.1, label: 'Canvas DPR' },
  })

  // Helper: get valid diamond shape indices for a setting style
  function getValidDiamondShapeIndices(settingStyle) {
    switch (settingStyle) {
      case 0: // Four Prongs
        return [0, 1, 2, 3, 5, 7, 8];
      case 1: // Six Prongs
        return [0, 6];
      case 2: // Classic Basket
        return [0,1,2,3,4,5,6,7,8,9];
      case 3: // Pavé Halo
        return [0, 7];
      case 4: // Pavé Basket
        return [0,1,2,3,4,5,6,7,8,9];
      case 5: // 4-P Bloom
        return [0,1,2,3,4,5,6,7,8]; // All shapes except Heart enabled for 4-P Bloom
      case 6: // 6-P Bloom
        return [0, 6]; // Only Round and Marquise
      default:
        return [0,1,2,3,4,5,6,7,8,9]; // All enabled for other styles
    }
  }

  // When setting style changes, ensure diamond shape is valid
  useEffect(() => {
    const validIndices = getValidDiamondShapeIndices(selectedSettingStyle);
    if (!validIndices.includes(selectedDiamondShape)) {
      setSelectedDiamondShape(validIndices[0]);
    }
  }, [selectedSettingStyle]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'q' || e.key === 'Q') {
        setLevaVisible((v) => !v);
        setShowStats((s) => !s);
      }
      if (e.key === 'd' || e.key === 'D') {
        setShowOnlyDiamond((v) => !v);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Screenshot function for Three.js canvas
  const handleScreenshot = () => {
    const canvas = document.querySelector('canvas');
    if (canvas) {
      // Force a render by dispatching a custom event
      const renderEvent = new CustomEvent('forceRender');
      window.dispatchEvent(renderEvent);
      
      // Wait for next frame and capture
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Create a temporary link element
          const link = document.createElement('a');
          link.download = `ring-configurator-${Date.now()}.png`;
          
          // Convert canvas to blob and create download link
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              link.href = url;
              link.click();
              URL.revokeObjectURL(url);
            } else {
              console.error('Failed to capture canvas');
              alert('Failed to capture screenshot. Please try again.');
            }
          }, 'image/png', 1.0);
        });
      });
    }
  };

  // State for details modal
  const [showDetails, setShowDetails] = useState(false);

  // Dummy pricing data
  const pricing = {
    diamondShapes: {
      'Round': 2000,
      'Princess': 1800,
      'Oval': 1900,
      'Emerald': 1700,
      'Pear': 1850,
      'Cushion': 1750,
      'Marquise': 1950,
      'Asscher': 1650,
      'Radiant': 1850,
      'Heart': 1900
    },
    diamondSizes: {
      '1ct': 2000,
      '1.5ct': 3000,
      '2ct': 4500
    },
    settingStyles: {
      'Four Prongs': 500,
      'Six Prongs': 600,
      'Classic Basket': 400,
      'Pavé Halo': 800,
      'Pavé Basket': 700,
      '4-P Bloom': 900,
      '6-P Bloom': 1000,
      'Pear 3 Stone': 1200,
      'Cathedral': 600,
      'Cathedral Pavé': 1000
    },
    bandStyles: {
      'Solitaire': 300,
      'French Pavé': 800,
      'U Shaped Pavé': 900,
      'Wide Solitaire': 400
    },
    metals: {
      'White Gold': 0,
      'Yellow Gold': 100,
      'Rose Gold': 150,
      'Platinum': 200
    }
  };

  const handleDetailsClick = () => {
    setShowDetails(!showDetails);
  };

  // Camera view state
  const [cameraView, setCameraView] = useState('front'); // 'front', 'top', 'side', or '360'
    const [isAutoRotating, setIsAutoRotating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStartTime] = useState(Date.now());
  const [hasShownLoading, setHasShownLoading] = useState(false);
  const [showOnlyDiamond, setShowOnlyDiamond] = useState(false);
  
  // Setting and Band Styles with icons
    const settingStyles = [
    { label: 'Four Prongs', icon: '/icons/Settings/4 prongs.png' },
    { label: 'Six Prongs', icon: '/icons/Settings/6 Prongs.png' },
    { label: 'Classic Basket', icon: '/icons/Settings/Classic Basket.png' },
    { label: 'Pavé Halo', icon: '/icons/Settings/Pave Halo.png' },
    { label: 'Pavé Basket', icon: '/icons/Settings/Pave Basket.png' },
    { label: '4-P Bloom', icon: '/icons/Settings/4 P Bloom.png' },
    { label: '6-P Bloom', icon: '/icons/Settings/6 P Bloom.png' },
    { label: 'Pear 3 Stone', icon: '/icons/Settings/Pear 3 Stones.png' },
    { label: 'Cathedral', icon: '/icons/Settings/Cathedral_.png' },
    { label: 'Cathedral Pavé', icon: '/icons/Settings/Cathedral Pave.png' }
  ];

  const bandStyles = [
    { label: 'Solitaire', icon: '/icons/Bands/Solitaire.png' },
    { label: 'French Pave', icon: '/icons/Bands/French Pave.png' },
    { label: 'U Shaped Pave', icon: '/icons/Bands/U Shape Pave.png' },
    { label: 'Wide Solitaire', icon: '/icons/Bands/Wide Solitaire.png' }
  ];
  
  // Auto-select annotation when Cathedral or Cathedral Pavé is selected, but maintain previous selection when possible
  useEffect(() => {
    if (selectedSettingStyle === 8 || selectedSettingStyle === 9) {
      // If no annotation is currently selected, set a default
      if (!selectedCathedralAnnotation) {
        if (selectedDiamondShape === 9) {
          setSelectedCathedralAnnotation('classic-basket');
        } else {
          setSelectedCathedralAnnotation('pave-halo');
        }
      } else {
        // If an annotation is already selected, check if it's still valid for the current diamond shape
        if (selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-halo') {
          // Heart diamond doesn't support Pavé Halo, so switch to Classic Basket
          setSelectedCathedralAnnotation('classic-basket');
        }
        // For other diamond shapes, keep the current selection
      }
    }
  }, [selectedSettingStyle, selectedDiamondShape]);

  // Handle loading state with proper progress integration
  const { progress, active } = useProgress();
  
  // Debug logging
  console.log('Loading state:', { progress, active, isLoading });
  
  // Simple, reliable loading state management
  useEffect(() => {
    // Show React loading screen immediately
    setIsLoading(true);
    setHasShownLoading(true);
    console.log('React loading screen activated');
  }, []);
  
  // Force loading screen to show for at least 1 second
  useEffect(() => {
    if (!hasShownLoading) {
      setIsLoading(true);
      setHasShownLoading(true);
      
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [hasShownLoading]);
  
  // Hide loading only when 3D content is fully loaded
  useEffect(() => {
    if (progress >= 100 && !active) {
      // Add a small delay to ensure everything is rendered
      const timer = setTimeout(() => {
        // Hide HTML loading screen
        const htmlLoading = document.getElementById('initial-loading');
        if (htmlLoading) {
          htmlLoading.style.display = 'none';
        }
        
        // Hide React loading screen
        setIsLoading(false);
        console.log('Loading screen hidden - 3D content ready');
        
        // Signal to HTML that React is ready
        window.dispatchEvent(new Event('react-ready'));
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [progress, active]);
  
  // Fallback: hide loading after 8 seconds maximum
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        console.log('Loading screen hidden - fallback timeout');
      }
    }, 8000);
    
    return () => clearTimeout(timer);
  }, [isLoading]);

  const handleViewCycle = () => {
    const views = ['front', 'top', 'side', '360'];
    const currentIndex = views.indexOf(cameraView);
    const nextIndex = (currentIndex + 1) % views.length;
    const nextView = views[nextIndex];
    
    if (nextView === '360') {
      setCameraView('360');
      setIsAutoRotating(true);
    } else {
      setCameraView(nextView);
      setIsAutoRotating(false);
    }
  };

  return (
    <div className="app-container" style={{ 
      background: 'radial-gradient(circle at center, #ffffff 0%, #e0e0e0 50%, #c0c0c0 100%)',
      minHeight: '100vh',
      width: '100vw',
      overflow: 'hidden'
    }}>
      {/* Loading Overlay - Outside Canvas */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 999999,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <LoadingBar />
        </div>
      )}

      <div className="canvas-container" style={{ 
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        background: 'radial-gradient(circle at center, #ffffff 0%, #e0e0e0 50%, #c0c0c0 100%)'
      }}>
        {/* View Cycle Button */}
        <button
          onClick={handleViewCycle}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '50%',
            width: '44px',
            height: '44px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '14px',
            fontWeight: '600',
            fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.25)';
            e.target.style.border = '1px solid rgba(255, 255, 255, 0.5)';
            e.target.style.transform = 'translateY(-2px) scale(1.05)';
            e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
            e.target.style.color = 'rgba(255, 255, 255, 1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.15)';
            e.target.style.border = '1px solid rgba(255, 255, 255, 0.3)';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            e.target.style.color = 'rgba(255, 255, 255, 0.9)';
          }}
        >
          {cameraView === 'front' ? 'F' : 
           cameraView === 'top' ? 'T' : 
           cameraView === 'side' ? 'S' : 
           isAutoRotating ? '⏸' : '360'}
        </button>

        {/* Details Button */}
        <button
          onClick={handleDetailsClick}
          style={{
            position: 'absolute',
            top: '20px',
            left: '80px',
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '50%',
            width: '44px',
            height: '44px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '16px'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.25)';
            e.target.style.border = '1px solid rgba(255, 255, 255, 0.5)';
            e.target.style.transform = 'translateY(-2px) scale(1.05)';
            e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
            e.target.style.color = 'rgba(255, 255, 255, 1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.15)';
            e.target.style.border = '1px solid rgba(255, 255, 255, 0.3)';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            e.target.style.color = 'rgba(255, 255, 255, 0.9)';
          }}
        >
          ℹ
        </button>

        {/* Screenshot Download Button */}
        <button
          onClick={handleScreenshot}
          style={{
            position: 'absolute',
            top: '20px',
            left: '140px',
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '50%',
            width: '44px',
            height: '44px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '16px'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.25)';
            e.target.style.border = '1px solid rgba(255, 255, 255, 0.5)';
            e.target.style.transform = 'translateY(-2px) scale(1.05)';
            e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
            e.target.style.color = 'rgba(255, 255, 255, 1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.15)';
            e.target.style.border = '1px solid rgba(255, 255, 255, 0.3)';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            e.target.style.color = 'rgba(255, 255, 255, 0.9)';
          }}
        >
          ↓
        </button>

        {/* Cathedral Annotation Buttons - Only show when Cathedral or Cathedral Pavé is selected */}
        {(selectedSettingStyle === 8 || selectedSettingStyle === 9) && (
          <div style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? 'auto' : '20px',
            top: window.innerWidth <= 768 ? 'calc(90vh - 400px)' : 'auto',
            left: window.innerWidth <= 768 ? '50%' : '42%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '20px',
            zIndex: 1001,
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {/* Pavé Basket Button */}
            <button
              onClick={() => setSelectedCathedralAnnotation('pave-basket')}
              style={{
                background: selectedCathedralAnnotation === 'pave-basket' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
                border: selectedCathedralAnnotation === 'pave-basket' ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                width: '80px',
                height: '80px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(8px)',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                color: 'rgba(0, 0, 0, 0.9)',
                fontSize: '12px',
                fontWeight: '500',
                fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                textAlign: 'center',
                lineHeight: '1.2'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                e.target.style.border = '1px solid rgba(255, 255, 255, 0.5)';
                e.target.style.transform = 'translateY(-2px) scale(1.05)';
                e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                e.target.style.color = 'rgba(0, 0, 0, 1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = selectedCathedralAnnotation === 'pave-basket' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)';
                e.target.style.border = selectedCathedralAnnotation === 'pave-basket' ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                e.target.style.color = 'rgba(0, 0, 0, 0.9)';
              }}
            >
              Pavé<br />Basket
            </button>

            {/* Classic Basket Button */}
            <button
              onClick={() => setSelectedCathedralAnnotation('classic-basket')}
              style={{
                background: selectedCathedralAnnotation === 'classic-basket' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
                border: selectedCathedralAnnotation === 'classic-basket' ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                width: '80px',
                height: '80px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(8px)',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                color: 'rgba(0, 0, 0, 0.9)',
                fontSize: '12px',
                fontWeight: '500',
                fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                textAlign: 'center',
                lineHeight: '1.2'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                e.target.style.border = '1px solid rgba(255, 255, 255, 0.5)';
                e.target.style.transform = 'translateY(-2px) scale(1.05)';
                e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                e.target.style.color = 'rgba(0, 0, 0, 1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = selectedCathedralAnnotation === 'classic-basket' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)';
                e.target.style.border = selectedCathedralAnnotation === 'classic-basket' ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                e.target.style.color = 'rgba(0, 0, 0, 0.9)';
              }}
            >
              Classic<br />Basket
            </button>

            {/* Pavé Halo Button - Disabled for Heart diamond shape */}
            <button
              onClick={selectedDiamondShape === 9 ? undefined : () => setSelectedCathedralAnnotation('pave-halo')}
              style={{
                background: selectedCathedralAnnotation === 'pave-halo' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
                border: selectedCathedralAnnotation === 'pave-halo' ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                width: '80px',
                height: '80px',
                cursor: selectedDiamondShape === 9 ? 'not-allowed' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(8px)',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                color: selectedDiamondShape === 9 ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.9)',
                fontSize: '12px',
                fontWeight: '500',
                fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                textAlign: 'center',
                lineHeight: '1.2',
                opacity: selectedDiamondShape === 9 ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (selectedDiamondShape !== 9) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                  e.target.style.border = '1px solid rgba(255, 255, 255, 0.5)';
                  e.target.style.transform = 'translateY(-2px) scale(1.05)';
                  e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                  e.target.style.color = 'rgba(0, 0, 0, 1)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.background = selectedCathedralAnnotation === 'pave-halo' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)';
                e.target.style.border = selectedCathedralAnnotation === 'pave-halo' ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                e.target.style.color = selectedDiamondShape === 9 ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.9)';
              }}
            >
              Pavé<br />Halo
            </button>
          </div>
        )}



        {/* Details Modal */}
        {showDetails && (
          <div
            style={{
              position: 'absolute',
              top: '80px',
              left: '20px',
              zIndex: 1001,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              borderRadius: '20px',
              padding: '28px',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1)',
              minWidth: '360px',
              maxWidth: '420px',
              fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '15px',
              color: '#1a202c',
              border: '1px solid rgba(255, 255, 255, 0.6)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '22px', 
                fontWeight: '700', 
                background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em'
              }}>
                Ring Specifications
              </h3>
              <button
                onClick={handleDetailsClick}
                style={{
                  background: 'rgba(226, 232, 240, 0.8)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(8px)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                  e.target.style.color = '#dc2626';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(226, 232, 240, 0.8)';
                  e.target.style.color = '#64748b';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                ×
              </button>
            </div>

            {/* Diamond Details */}
            <div style={{ 
              marginBottom: '20px',
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 197, 253, 0.05) 100%)',
              borderRadius: '16px',
              border: '1px solid rgba(59, 130, 246, 0.1)'
            }}>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '17px', 
                fontWeight: '600', 
                color: '#1e40af',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>💎</span>
                Diamond
              </h4>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontWeight: '500' }}>Shape:</span>
                <span style={{ color: '#1a202c', fontWeight: '600' }}>{shapeLabels[selectedDiamondShape]}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontWeight: '500' }}>Size:</span>
                <span style={{ color: '#1a202c', fontWeight: '600' }}>{diamondSizeData[selectedDiamondSize].label}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0px', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontWeight: '500' }}>Price:</span>
                <span style={{ color: '#059669', fontWeight: '700', fontSize: '16px' }}>
                  ${(pricing.diamondShapes[shapeLabels[selectedDiamondShape]] + pricing.diamondSizes[diamondSizeData[selectedDiamondSize].label]).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Setting Details */}
            <div style={{ 
              marginBottom: '20px',
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.05) 0%, rgba(196, 181, 253, 0.05) 100%)',
              borderRadius: '16px',
              border: '1px solid rgba(168, 85, 247, 0.1)'
            }}>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '17px', 
                fontWeight: '600', 
                color: '#7c3aed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>⚙️</span>
                Setting
              </h4>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontWeight: '500' }}>Style:</span>
                <span style={{ color: '#1a202c', fontWeight: '600' }}>{settingStyles[selectedSettingStyle].label}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontWeight: '500' }}>Metal:</span>
                <span style={{ color: '#1a202c', fontWeight: '600' }}>{metalNames[selectedSettingMetal]}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0px', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontWeight: '500' }}>Price:</span>
                <span style={{ color: '#059669', fontWeight: '700', fontSize: '16px' }}>
                  ${(pricing.settingStyles[settingStyles[selectedSettingStyle].label] + pricing.metals[metalNames[selectedSettingMetal]]).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Band Details */}
            <div style={{ 
              marginBottom: '20px',
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.05) 0%, rgba(251, 207, 232, 0.05) 100%)',
              borderRadius: '16px',
              border: '1px solid rgba(236, 72, 153, 0.1)'
            }}>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '17px', 
                fontWeight: '600', 
                color: '#be185d',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>💍</span>
                Band
              </h4>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontWeight: '500' }}>Style:</span>
                <span style={{ color: '#1a202c', fontWeight: '600' }}>{bandStyles[selectedBandStyle].label}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontWeight: '500' }}>Metal:</span>
                <span style={{ color: '#1a202c', fontWeight: '600' }}>{metalNames[selectedBandMetal]}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0px', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontWeight: '500' }}>Price:</span>
                <span style={{ color: '#059669', fontWeight: '700', fontSize: '16px' }}>
                  ${(pricing.bandStyles[bandStyles[selectedBandStyle].label] + pricing.metals[metalNames[selectedBandMetal]]).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Total Price */}
            <div style={{ 
              borderTop: '2px solid rgba(59, 130, 246, 0.1)', 
              paddingTop: '20px', 
              marginTop: '20px',
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              fontSize: '18px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 197, 253, 0.05) 100%)',
              padding: '16px',
              borderRadius: '16px',
              border: '1px solid rgba(59, 130, 246, 0.15)'
            }}>
              <span style={{ color: '#1a202c' }}>Total:</span>
              <span style={{ 
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: '22px',
                fontWeight: '800',
                letterSpacing: '-0.02em'
              }}>
                ${(
                  pricing.diamondShapes[shapeLabels[selectedDiamondShape]] + 
                  pricing.diamondSizes[diamondSizeData[selectedDiamondSize].label] +
                  pricing.settingStyles[settingStyles[selectedSettingStyle].label] +
                  pricing.metals[metalNames[selectedSettingMetal]] +
                  pricing.bandStyles[bandStyles[selectedBandStyle].label] +
                  pricing.metals[metalNames[selectedBandMetal]]
                ).toLocaleString()}
              </span>
            </div>
          </div>
        )}
        
        <Leva hidden={!levaVisible} position="left" />
        <LogAndromedaNodes />
      <Canvas
        ref={canvasRef}
        shadows
        camera={{ position: [0, 3, 5], fov: 50 }}
        style={{ 
          width: '100%', 
          height: '100%',
          background: 'transparent',
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.5s ease-in-out'
        }}
        dpr={dpr}
        gl={{ 
          localClippingEnabled: true,
          alpha: true,
          antialias: true,
          powerPreference: "high-performance"
        }}
      >
        {showStats && <Stats position="top-left" />}
        <Suspense fallback={null}>
          <Scene 
            selectedNode={selectedNode} 
            selectedSettingStyle={selectedSettingStyle} 
            selectedBandStyle={selectedBandStyle} 
            selectedSettingMetal={selectedSettingMetal} 
            metals={metals} 
            selectedBandMetal={selectedBandMetal} 
            selectedDiamondShape={selectedDiamondShape}
            selectedDiamondSize={selectedDiamondSize}
            diamondSizeData={diamondSizeData}
            cameraView={cameraView}
            isAutoRotating={isAutoRotating}
            selectedCathedralAnnotation={selectedCathedralAnnotation}
            setSelectedCathedralAnnotation={setSelectedCathedralAnnotation}
            showOnlyDiamond={showOnlyDiamond}
          />
        </Suspense>
      </Canvas>
      </div>
      <div className="sidebar-container">
        <RingCustomizerSidebar
          selectedDiamondShape={selectedDiamondShape}
          setSelectedDiamondShape={setSelectedDiamondShape}
          selectedSettingStyle={selectedSettingStyle}
          setSelectedSettingStyle={setSelectedSettingStyle}
          selectedBandStyle={selectedBandStyle}
          setSelectedBandStyle={setSelectedBandStyle}
          selectedSettingMetal={selectedSettingMetal}
          setSelectedSettingMetal={setSelectedSettingMetal}
          selectedBandMetal={selectedBandMetal}
          setSelectedBandMetal={setSelectedBandMetal}
          metals={metals}
          metalNames={metalNames}
          selectedDiamondSize={selectedDiamondSize}
          setSelectedDiamondSize={setSelectedDiamondSize}
          diamondSizeData={diamondSizeData}
          settingStyles={settingStyles}
          bandStyles={bandStyles}
        />
      </div>
    </div>
  )
}

// Replace RadiantDiamondOnly with ComparisonDiamond that shows the selected diamond only (no head)
function ComparisonDiamond({ diamondEnv, metalEnv, diamondProps, selectedNode, clippingPlane, selectedSettingStyle, selectedBandStyle, selectedSettingMetal, metals, selectedBandMetal, selectedDiamondShape, selectedDiamondSize, diamondSizeData, selectedCathedralAnnotation, showOnlyDiamond, envMapIntensityGlobal, metalEnvMapIntensity }) {
  // Debug: log selectedBandStyle
  console.log('selectedBandStyle:', selectedBandStyle);
  // Define shapeLabels and shapeLabel at the top so it is always available
  const shapeLabels = [
    'Round',    // 0
    'Princess', // 1
    'Oval',     // 2
    'Emerald',  // 3
    'Pear',     // 4
    'Cushion',  // 5
    'Marquise', // 6
    'Asscher',  // 7
    'Radiant',  // 8
    'Heart'     // 9
  ];
  const shapeLabel = shapeLabels[selectedDiamondShape];
  const diamondShapeNodeNames = [
    '1_Round',      // 0
    '1_Princess',   // 1
    '1_Oval',       // 2
    '1_Emerald',    // 3
    '1_Pear',       // 4
    '1_Cushion',    // 5
    '1_Marquise',   // 6
    '1_Asscher',    // 7
    '1_Radiant',    // 8
    '1_Heart'       // 9
  ];
  const { nodes } = useGLTF('/Andromeda.glb');
  // Render all nodes starting with 2_ (e.g., pave halo parts)
  const paveHaloNodes = Object.entries(nodes)
    .filter(([key]) => key.startsWith('2_'))
    .map(([, node]) => node);
  let diamondNode = null;
  if (selectedNode.startsWith('1_')) {
    diamondNode = nodes[selectedNode];
  }
  
  // Helper to recursively render all meshes with geometry under a node
  function renderNodeMeshes(node, keyPrefix = '') {
    if (!node) return null;
    // If node has geometry, render it
    if (node.geometry) {
      // Debug: print mesh name and bounding box center
      let center = null;
      try {
        node.geometry.computeBoundingBox();
        center = node.geometry.boundingBox.getCenter(new THREE.Vector3());
      } catch (e) {}
      console.log('Rendering mesh:', node.name, 'center:', center);
      // Special material logic for pave halo
      if (node.name === '2_PaveHalo_Round_Diamonds') {
        // Use MeshRefractionMaterial for diamonds
        return (
          <mesh geometry={node.geometry.clone()} key={keyPrefix + node.name} castShadow receiveShadow>
            <MeshRefractionMaterial
              envMap={diamondEnv}
              bounces={diamondProps.bounces}
              ior={diamondProps.ior}
              fresnel={diamondProps.fresnel}
              aberrationStrength={diamondProps.aberrationStrength}
              color={diamondProps.color}
              side={THREE.DoubleSide}
              toneMapped={true}
              envMapIntensity={envMapIntensityGlobal}
              {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
              clipShadows
            />
          </mesh>
        );
      } else if (node.name === '2_PaveHalo_Round') {
        // Use meshPhysicalMaterial for basket, do NOT apply clipping plane
        return (
          <mesh geometry={node.geometry.clone()} key={keyPrefix + node.name} castShadow receiveShadow>
            <meshPhysicalMaterial
              color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
              roughness={headProps.roughness}
              metalness={headProps.metalness}
              clearcoat={headProps.clearcoat}
              clearcoatRoughness={headProps.clearcoatRoughness}
              transmission={headProps.transmission}
              ior={headProps.ior}
              thickness={headProps.thickness}
              envMap={metalEnv}
              envMapIntensity={envMapIntensityGlobal * metalEnvMapIntensity}
              clipShadows
            />
          </mesh>
        );
  } else {
        // Default: meshPhysicalMaterial (hot pink for debug, or headProps.color for production)
        return (
          <mesh geometry={node.geometry.clone()} key={keyPrefix + node.name} castShadow receiveShadow>
            <meshPhysicalMaterial
              color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
              roughness={headProps.roughness}
              metalness={headProps.metalness}
              clearcoat={headProps.clearcoat}
              clearcoatRoughness={headProps.clearcoatRoughness}
              transmission={headProps.transmission}
              ior={headProps.ior}
              thickness={headProps.thickness}
              envMap={metalEnv}
              envMapIntensity={envMapIntensityGlobal * metalEnvMapIntensity}
              {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
              clipShadows
            />
          </mesh>
        );
      }
    }
    // If node has children, recursively render their meshes
    if (node.children && node.children.length > 0) {
      return node.children.map((child, idx) => renderNodeMeshes(child, keyPrefix + node.name + '-' + idx + '-'));
    }
    return null;
  }

  // Leva controls for Head Material
  const headProps = useControls('Head Material', {
    color: { value: '#ffffff', label: 'Head Color' },
    roughness: { value: 0, min: 0, max: 1, step: 0.01, label: 'Roughness' },
    metalness: { value: 1, min: 0, max: 1, step: 0.01, label: 'Metalness' },
    clearcoat: { value: 0, min: 0, max: 1, step: 0.01, label: 'Clearcoat' },
    clearcoatRoughness: { value: 0, min: 0, max: 1, step: 0.01, label: 'Clearcoat Roughness' },
    transmission: { value: 0, min: 0, max: 1, step: 0.01, label: 'Transmission' },
    ior: { value: 0, min: 0, max: 2.5, step: 0.01, label: 'IOR' },
    thickness: { value: 0, min: 0, max: 2, step: 0.01, label: 'Thickness' },
  });

  // Calculate scale based on diamond size - apply size multiplier to the diamond and basket
  const baselineWidth = diamondSizeData[0].w; // 1ct width as baseline
  const diamondWidth = diamondSizeData[selectedDiamondSize].w;
  const diamondSizeScale = diamondWidth / baselineWidth;
  
  // Base scale for the ring
  const baseScale = selectedNode.startsWith('Diamond_Round')
    ? [0.55/3*0.3*2*0.64, 0.55/3*0.3*2*0.64, 0.55/3*0.3*2*0.64]
    : [0.45/3*0.3*2*0.64, 0.45/3*0.3*2*0.64, 0.45/3*0.3*2*0.64];
  
  // Apply diamond size scaling to the entire ring (diamond + basket)
  const scale = [
    baseScale[0] * diamondSizeScale,
    baseScale[1] * diamondSizeScale,
    baseScale[2] * diamondSizeScale
  ];

  // Rotate so GLB faces up (Z+ up)
  const rotation = [Math.PI / 2, 0, 0];

  // General Pavé Basket logic for all shapes
  let paveBasketDiamonds = null;
  let paveBasketBasket = null;
  if (selectedSettingStyle === 4) {
    const diamondsNode = nodes[`2_PaveBasket_${shapeLabel}_Diamonds`] || nodes['2_PaveBasket_Round_Diamonds'];
    const basketNode = nodes[`2_PaveBasket_${shapeLabel}`] || nodes['2_PaveBasket_Round'];
    if (diamondsNode && diamondsNode.geometry) {
      paveBasketDiamonds = (
        <mesh geometry={diamondsNode.geometry.clone()} key={diamondsNode.name} castShadow receiveShadow>
          <MeshRefractionMaterial
            envMap={diamondEnv}
            bounces={diamondProps.bounces}
            ior={diamondProps.ior}
            fresnel={diamondProps.fresnel}
            aberrationStrength={diamondProps.aberrationStrength}
            color={diamondProps.color}
            side={THREE.DoubleSide}
            toneMapped={true}
            envMapIntensity={envMapIntensityGlobal}
            {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
            clipShadows
          />
        </mesh>
      );
    }
    if (basketNode && basketNode.geometry) {
      paveBasketBasket = (
        <mesh geometry={basketNode.geometry.clone()} key={basketNode.name} castShadow receiveShadow>
          <meshPhysicalMaterial
            color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
            roughness={headProps.roughness}
            metalness={headProps.metalness}
            clearcoat={headProps.clearcoat}
            clearcoatRoughness={headProps.clearcoatRoughness}
            transmission={headProps.transmission}
            ior={headProps.ior}
            thickness={headProps.thickness}
            envMap={metalEnv}
            envMapIntensity={envMapIntensityGlobal * metalEnvMapIntensity}
            clipShadows
          />
        </mesh>
      );
    }
  }

  // Four Prongs mesh logic
  let fourProngsMesh = null;
  if (selectedSettingStyle === 0 || selectedSettingStyle === 2) {
    const fourProngsShapeLabels = [
      'Round', 'Princess', 'Oval', 'Emerald', 'Cushion', 'Asscher', 'Radiant'
    ];
    const enabledFourProngs = ['Round','Princess','Oval','Emerald','Cushion','Asscher','Radiant'];
    const shapeIndexMap = {
      0: 'Round',
      1: 'Princess',
      2: 'Oval',
      3: 'Emerald',
      5: 'Cushion',
      7: 'Asscher',
      8: 'Radiant',
    };
    const selectedShapeIdx = diamondShapeNodeNames.indexOf(selectedNode);
    const selectedShapeLabel = shapeIndexMap[selectedShapeIdx];
    if (enabledFourProngs.includes(selectedShapeLabel)) {
      const nodeName = `2_4P_${selectedShapeLabel}`;
      const node = nodes[nodeName];
      if (node && node.geometry) {
        fourProngsMesh = (
          <mesh geometry={node.geometry.clone()} key={nodeName} castShadow receiveShadow>
            <meshPhysicalMaterial
              color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
              roughness={headProps.roughness}
              metalness={headProps.metalness}
              clearcoat={headProps.clearcoat}
              clearcoatRoughness={headProps.clearcoatRoughness}
              transmission={headProps.transmission}
              ior={headProps.ior}
              thickness={headProps.thickness}
              envMap={metalEnv}
              envMapIntensity={envMapIntensityGlobal * metalEnvMapIntensity}
              clipShadows
            />
          </mesh>
        );
      }
    }
  }

  // Six Prongs mesh logic
  let sixProngsMesh = null;
  if (selectedSettingStyle === 1) {
    // Only indices 0 (Round) and 6 (Marquise) are enabled
    const sixProngsShapeLabels = [
      'Round', 'Princess', 'Oval', 'Emerald', 'Pear', 'Cushion', 'Marquise', 'Asscher', 'Radiant', 'Heart'
    ];
    const selectedShapeLabel = sixProngsShapeLabels[selectedNode ? diamondShapeNodeNames.indexOf(selectedNode) : 0];
    const enabledSixProngs = ['Round', 'Marquise'];
    if (enabledSixProngs.includes(selectedShapeLabel)) {
      const nodeName = `2_6P_${selectedShapeLabel}`;
      const node = nodes[nodeName];
      if (node && node.geometry) {
        sixProngsMesh = (
          <mesh geometry={node.geometry.clone()} key={nodeName} castShadow receiveShadow>
            <meshPhysicalMaterial
              color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
              roughness={headProps.roughness}
              metalness={headProps.metalness}
              clearcoat={headProps.clearcoat}
              clearcoatRoughness={headProps.clearcoatRoughness}
              transmission={headProps.transmission}
              ior={headProps.ior}
              thickness={headProps.thickness}
              envMap={metalEnv}
              envMapIntensity={envMapIntensityGlobal * metalEnvMapIntensity}
              clipShadows
            />
          </mesh>
        );
      }
    }
  }

  // Classic Basket mesh logic
  let classicBasketMesh = null;
  if (selectedSettingStyle === 2) {
    // Indices 0-9 are enabled
    const classicBasketShapeLabels = [
      'Round', 'Princess', 'Oval', 'Emerald', 'Pear', 'Cushion', 'Marquise', 'Asscher', 'Radiant', 'Heart'
    ];
    const selectedShapeLabel = classicBasketShapeLabels[selectedNode ? diamondShapeNodeNames.indexOf(selectedNode) : 0];
    const enabledClassicBasket = ['Asscher', 'Cushion', 'Emerald', 'Heart', 'Marquise', 'Oval', 'Pear', 'Princess', 'Radiant', 'Round'];
    if (enabledClassicBasket.includes(selectedShapeLabel)) {
      const nodeName = `2_ClassicBasket_${selectedShapeLabel}`;
      const node = nodes[nodeName];
      if (node && node.geometry) {
        classicBasketMesh = (
          <mesh geometry={node.geometry.clone()} key={nodeName} castShadow receiveShadow>
            <meshPhysicalMaterial
              color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
              roughness={headProps.roughness}
              metalness={headProps.metalness}
              clearcoat={headProps.clearcoat}
              clearcoatRoughness={headProps.clearcoatRoughness}
              transmission={headProps.transmission}
              ior={headProps.ior}
              thickness={headProps.thickness}
              envMap={metalEnv}
              envMapIntensity={envMapIntensityGlobal * metalEnvMapIntensity}
              clipShadows
            />
          </mesh>
        );
      }
    }
  }

  // 4-P Bloom mesh logic
  let fourPBloomMesh = null;
  if (selectedSettingStyle === 5) {
    // Indices 0-8 are enabled for 4-P Bloom (Heart excluded)
    const fourPBloomShapeLabels = [
      'Round', 'Princess', 'Oval', 'Emerald', 'Pear', 'Cushion', 'Marquise', 'Asscher', 'Radiant', 'Heart'
    ];
    const selectedShapeLabel = fourPBloomShapeLabels[selectedNode ? diamondShapeNodeNames.indexOf(selectedNode) : 0];
    const enabledFourPBloom = ['Round', 'Princess', 'Oval', 'Emerald', 'Pear', 'Cushion', 'Marquise', 'Asscher', 'Radiant'];
    if (enabledFourPBloom.includes(selectedShapeLabel)) {
      const nodeName = `2_4PBloom_${selectedShapeLabel}`;
      const node = nodes[nodeName];
      if (node && node.geometry) {
        fourPBloomMesh = (
          <mesh geometry={node.geometry.clone()} key={nodeName} castShadow receiveShadow>
            <meshPhysicalMaterial
              color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
              roughness={headProps.roughness}
              metalness={headProps.metalness}
              clearcoat={headProps.clearcoat}
              clearcoatRoughness={headProps.clearcoatRoughness}
              transmission={headProps.transmission}
              ior={headProps.ior}
              thickness={headProps.thickness}
              envMap={metalEnv}
              envMapIntensity={envMapIntensityGlobal * metalEnvMapIntensity}
              clipShadows
            />
          </mesh>
        );
      }
    }
  }

  // 6-P Bloom mesh logic
  let sixPBloomMesh = null;
  if (selectedSettingStyle === 6) {
    // Only Round and Marquise are enabled
    const sixPBloomShapeLabels = [
      'Round', 'Princess', 'Oval', 'Emerald', 'Pear', 'Cushion', 'Marquise', 'Asscher', 'Radiant', 'Heart'
    ];
    const selectedShapeLabel = sixPBloomShapeLabels[selectedNode ? diamondShapeNodeNames.indexOf(selectedNode) : 0];
    const enabledSixPBloom = ['Round', 'Marquise'];
    if (enabledSixPBloom.includes(selectedShapeLabel)) {
      const nodeName = `2_6PBloom_${selectedShapeLabel}`;
      const node = nodes[nodeName];
      if (node && node.geometry) {
        sixPBloomMesh = (
          <mesh geometry={node.geometry.clone()} key={nodeName} castShadow receiveShadow>
            <meshPhysicalMaterial
              color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
              roughness={headProps.roughness}
              metalness={headProps.metalness}
              clearcoat={headProps.clearcoat}
              clearcoatRoughness={headProps.clearcoatRoughness}
              transmission={headProps.transmission}
              ior={headProps.ior}
              thickness={headProps.thickness}
              envMap={metalEnv}
              envMapIntensity={envMapIntensityGlobal * metalEnvMapIntensity}
              clipShadows
            />
          </mesh>
        );
      }
    }
  }

  // --- Custom logic for Pear 3 Stone + Heart ---
  let pear3StoneHeartMesh = null;
  if (selectedSettingStyle === 7 && selectedDiamondShape === 9) {
    const node = nodes['2_Pear3Stone_Heart'];
    if (node && node.geometry) {
      pear3StoneHeartMesh = (
        <mesh geometry={node.geometry.clone()} key={'2_Pear3Stone_Heart'} castShadow receiveShadow>
          <meshPhysicalMaterial
            color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
            roughness={headProps.roughness}
            metalness={headProps.metalness}
            clearcoat={headProps.clearcoat}
            clearcoatRoughness={headProps.clearcoatRoughness}
            transmission={headProps.transmission}
            ior={headProps.ior}
            thickness={headProps.thickness}
            envMap={metalEnv}
            envMapIntensity={envMapIntensityGlobal * metalEnvMapIntensity}
            clipShadows
          />
        </mesh>
      );
    }
  }

  // Render 3_Solitaire for Solitaire band style
  const showSolitaireBand = selectedBandStyle === 0 && nodes['3_Solitaire'];

  // Render 3_French_Pave and 3_French_Pave_Diamonds for French Pave band style
  const showFrenchPaveBand = selectedBandStyle === 1 && nodes['3_French_Pave'] && nodes['3_French_Pave_Diamonds'];

  // Render 3_U_Shaped_Pave and 3_U_Shaped_Pave_Diamonds for U Shaped Pave band style
  const showUShapedPaveBand = selectedBandStyle === 2 && nodes['3_U_Shaped_Pave'] && nodes['3_U_Shaped_Pave_Diamonds'];

  // Render 3_Cathedral_Solitaire for Channel Set band style
  const showChannelSetBand = selectedBandStyle === 4 && nodes['3_Cathedral_Solitaire'];

  // Render 3_Wide_Solitaire for Wide Solitaire band style
  const showWideSolitaireBand = selectedBandStyle === 3 && nodes['3_Wide_Solitaire'];

  // Get mesh centers for dot visualization - MOVED UP
  let diamondCenter = new THREE.Vector3(0, 0, 0);
  let ringBodyCenter = new THREE.Vector3(0, 0, 0);

  if (diamondNode && diamondNode.geometry) {
    diamondNode.geometry.computeBoundingBox();
    diamondCenter = diamondNode.geometry.boundingBox.getCenter(new THREE.Vector3());
  }

  // Get ring body center from 3_Solitaire
  const ringBodyNode = nodes['3_Solitaire'];
  if (ringBodyNode && ringBodyNode.geometry) {
    ringBodyNode.geometry.computeBoundingBox();
    ringBodyCenter = ringBodyNode.geometry.boundingBox.getCenter(new THREE.Vector3());
  }

  // Determine active basket node first
  let activeBasketNodeName = null;
  let activeBasketNode = null;

  // Determine which basket is active based on setting style
  switch(selectedSettingStyle) {
    case 0: // Four Prongs
      activeBasketNodeName = `2_4P_${shapeLabel}`;
      break;
    case 1: // Six Prongs  
      activeBasketNodeName = `2_6P_${shapeLabel}`;
      break;
    case 2: // Classic Basket
      activeBasketNodeName = `2_ClassicBasket_${shapeLabel}`;
      break;
    case 3: // Pavé Halo
      activeBasketNodeName = `2_PaveHalo_${shapeLabel}`;
      break;
    case 4: // Pavé Basket
      activeBasketNodeName = `2_PaveBasket_${shapeLabel}`;
      break;
    case 5: // 4-P Bloom
      activeBasketNodeName = `2_4PBloom_${shapeLabel}`;
      break;
  }

  if (activeBasketNodeName) {
    activeBasketNode = nodes[activeBasketNodeName];
    if (activeBasketNode && activeBasketNode.geometry) {
      activeBasketNode.geometry.computeBoundingBox();
    }
  }

  // Calculate Z-axis distance between diamond center (red dot) and ring body center (blue dot)
  const scaledDiamondCenterZ = diamondCenter.z * diamondSizeScale;
  const ringBodyCenterZ = ringBodyCenter.z;
  const zAxisDistance = scaledDiamondCenterZ - ringBodyCenterZ;
  
  // Calculate baseline Z-distance at 1ct (index 0)
  const baselineDiamondSizeScale = 1.0; // Always 1.0 for 1ct
  const baselineScaledDiamondCenterZ = diamondCenter.z * baselineDiamondSizeScale;
  const baselineZAxisDistance = baselineScaledDiamondCenterZ - ringBodyCenterZ;
  
  // Calculate position adjustment needed to maintain baseline distance
  const zDistanceDifference = zAxisDistance - baselineZAxisDistance;
  const zPositionAdjustment = -zDistanceDifference + (baselineDiamondSizeScale - diamondSizeScale)*diamondSizeScale*2; // Subtract baseline scale
  
  // Only log diamond size related info
  console.log(`💎 DIAMOND SIZE LOG:`, {
    currentDiamondSize: diamondSizeData[selectedDiamondSize].label,
    currentDiamondScale: diamondSizeScale,
    current: {
      diamondCenterZ: scaledDiamondCenterZ,
      ringBodyCenterZ: ringBodyCenterZ,
      distance: zAxisDistance
    },
    baseline_1ct: {
      diamondCenterZ: baselineScaledDiamondCenterZ,
      distance: baselineZAxisDistance
    },
    adjustment: {
      difference: zAxisDistance - baselineZAxisDistance,
      positionAdjustment: zPositionAdjustment,
      note: 'Applied to diamond/basket group to maintain consistent connection'
    },
    final: {
      adjustedDiamondCenterZ: scaledDiamondCenterZ + zPositionAdjustment,
      finalDistanceToRingBody: (scaledDiamondCenterZ + zPositionAdjustment) - ringBodyCenterZ,
      shouldEqualBaseline: baselineZAxisDistance
    }
  });

  // Log active basket position for reference
  if (activeBasketNodeName) {
    if (activeBasketNode && activeBasketNode.geometry) {
      activeBasketNode.geometry.computeBoundingBox();
      const basketBbox = activeBasketNode.geometry.boundingBox;

    } else {
      console.log(`❌ ${activeBasketNodeName} not found in nodes`);
    }
  }

  // Pave Halo node references
  const diamondsNode = nodes[`2_PaveHalo_${shapeLabel}_Diamonds`] || nodes['2_PaveHalo_Round_Diamonds'];
  const basketNode = nodes[`2_PaveHalo_${shapeLabel}`] || nodes['2_PaveHalo_Round'];

  // --- Magnet Algorithm: Snap setting to band, no gap ---
  // 1. Identify active band mesh
  let bandMesh = null;
  if (showSolitaireBand && showSolitaireBand.geometry) bandMesh = showSolitaireBand;
  else if (showFrenchPaveBand) bandMesh = nodes['3_French_Pave'];
  else if (showUShapedPaveBand) bandMesh = nodes['3_U_Shaped_Pave'];
  else if (showChannelSetBand) bandMesh = nodes['3_Cathedral_Solitaire'];
  else if (showWideSolitaireBand) bandMesh = nodes['3_Wide_Solitaire'];

  // 2. Compute band top Z
  let bandTopZ = 0;
  if (bandMesh && bandMesh.geometry) {
    bandMesh.geometry.computeBoundingBox();
    console.log('Band bounding box:', bandMesh.geometry.boundingBox);
    // Wide Solitaire (selectedBandStyle === 3) uses standard positioning like other bands
      bandTopZ = bandMesh.geometry.boundingBox.min.z;
  }

  // 3. Identify basket/prongs mesh for the current setting style
  let basketMesh = null;
  switch(selectedSettingStyle) {
    case 0: // Four Prongs
      basketMesh = fourProngsMesh && fourProngsMesh.props && fourProngsMesh.props.geometry ? fourProngsMesh : null;
      break;
    case 1: // Six Prongs
      basketMesh = sixProngsMesh && sixProngsMesh.props && sixProngsMesh.props.geometry ? sixProngsMesh : null;
      break;
    case 2: // Classic Basket
      basketMesh = classicBasketMesh && classicBasketMesh.props && classicBasketMesh.props.geometry ? classicBasketMesh : null;
      break;
    case 3: // Pavé Halo
      basketMesh = basketNode && basketNode.geometry ? {props: {geometry: basketNode.geometry}} : null;
      break;
    case 4: // Pavé Basket
      basketMesh = paveBasketBasket && paveBasketBasket.props && paveBasketBasket.props.geometry ? paveBasketBasket : null;
      break;
    case 5: // 4-P Bloom
      basketMesh = fourPBloomMesh && fourPBloomMesh.props && fourPBloomMesh.props.geometry ? fourPBloomMesh : null;
      break;
    case 6: // 6-P Bloom
      basketMesh = sixPBloomMesh && sixPBloomMesh.props && sixPBloomMesh.props.geometry ? sixPBloomMesh : null;
      break;
    case 7: // Pear 3 Stone
      // For Pear 3 Stone, use the appropriate mesh based on diamond shape
      if (selectedDiamondShape === 0) { // Round
        basketMesh = nodes['2_Pear3Stone_Round'] && nodes['2_Pear3Stone_Round'].geometry ? 
          {props: {geometry: nodes['2_Pear3Stone_Round'].geometry}} : null;
      } else if (selectedDiamondShape === 9) { // Heart
        basketMesh = nodes['2_Pear3Stone_Heart'] && nodes['2_Pear3Stone_Heart'].geometry ? 
          {props: {geometry: nodes['2_Pear3Stone_Heart'].geometry}} : null;
      } else if (selectedDiamondShape === 7) { // Asscher
        basketMesh = nodes['2_Pear3Stone_Asscher'] && nodes['2_Pear3Stone_Asscher'].geometry ? 
          {props: {geometry: nodes['2_Pear3Stone_Asscher'].geometry}} : null;
      } else if (selectedDiamondShape === 5) { // Cushion
        basketMesh = nodes['2_Pear3Stone_Cushion'] && nodes['2_Pear3Stone_Cushion'].geometry ? 
          {props: {geometry: nodes['2_Pear3Stone_Cushion'].geometry}} : null;
      } else if (selectedDiamondShape === 3) { // Emerald
        basketMesh = nodes['2_Pear3Stone_Emerald'] && nodes['2_Pear3Stone_Emerald'].geometry ? 
          {props: {geometry: nodes['2_Pear3Stone_Emerald'].geometry}} : null;
      } else if (selectedDiamondShape === 1) { // Princess
        basketMesh = nodes['2_Pear3Stone_Princess'] && nodes['2_Pear3Stone_Princess'].geometry ? 
          {props: {geometry: nodes['2_Pear3Stone_Princess'].geometry}} : null;
      } else if (selectedDiamondShape === 2) { // Oval
        basketMesh = nodes['2_Pear3Stone_Oval'] && nodes['2_Pear3Stone_Oval'].geometry ? 
          {props: {geometry: nodes['2_Pear3Stone_Oval'].geometry}} : null;
      } else if (selectedDiamondShape === 4) { // Pear
        basketMesh = nodes['2_Pear3Stone_Pear'] && nodes['2_Pear3Stone_Pear'].geometry ? 
          {props: {geometry: nodes['2_Pear3Stone_Pear'].geometry}} : null;
      } else if (selectedDiamondShape === 6) { // Marquise
        basketMesh = nodes['2_Pear3Stone_Marquise'] && nodes['2_Pear3Stone_Marquise'].geometry ? 
          {props: {geometry: nodes['2_Pear3Stone_Marquise'].geometry}} : null;
      } else if (selectedDiamondShape === 8) { // Radiant
        basketMesh = nodes['2_Pear3Stone_Radiant'] && nodes['2_Pear3Stone_Radiant'].geometry ? 
          {props: {geometry: nodes['2_Pear3Stone_Radiant'].geometry}} : null;
      }
      break;
    default:
      basketMesh = null;
  }

  // 4. Compute basket bottom Z (after scaling)
  let basketBottomZ = 0;
  if (basketMesh && basketMesh.props && basketMesh.props.geometry) {
    const mesh = new THREE.Mesh(basketMesh.props.geometry);
    mesh.scale.set(diamondSizeScale, diamondSizeScale, diamondSizeScale);
    mesh.updateMatrixWorld();
    const box = new THREE.Box3().setFromObject(mesh);
    console.log('Basket bounding box:', box);

    if (selectedSettingStyle === 2 && selectedBandStyle === 3) {
      // Special case: Classic Basket + Wide Solitaire
      // Use a smaller base offset to eliminate gap
      const t = selectedDiamondSize / 5; // 0 for 1ct, 1 for 4ct
      const specialOffset = ((1.0 * t) + 2.4) * diamondSizeScale;
      // Shape-specific offset for Princess
      let shapeOffset = 0;
      if (selectedDiamondShape === 1) { // 1 = Princess
        shapeOffset = -0.5 * diamondSizeScale; // Tweak as needed
      } else if (selectedDiamondShape === 2) { // 2 = Marquise
        shapeOffset = 0.1 * diamondSizeScale; // Tweak as needed
        if(selectedDiamondSize === 0 )
          shapeOffset = -0.2 * diamondSizeScale;
        if(selectedDiamondSize === 1 )
          shapeOffset = -0.1 * diamondSizeScale;
      }  else if (selectedDiamondShape === 3) { // 3 
        shapeOffset = -0.6 * diamondSizeScale;
        if(selectedDiamondSize === 0 )
          shapeOffset = -0.7 * diamondSizeScale;
      } else if (selectedDiamondShape === 4) {
        shapeOffset = -2.3 * diamondSizeScale;
        if(selectedDiamondSize === 0 )
          shapeOffset = -2.6 * diamondSizeScale;
      } else if (selectedDiamondShape === 5) {
        shapeOffset = -0.5 * diamondSizeScale;
        if(selectedDiamondSize === 0 )
          shapeOffset = -0.8 * diamondSizeScale;
        if(selectedDiamondSize === 1 )
          shapeOffset = -0.6 * diamondSizeScale;
      } else if (selectedDiamondShape === 6) { 
        shapeOffset = -2.3 * diamondSizeScale;
        if(selectedDiamondSize === 0 )
          shapeOffset = -2.6 * diamondSizeScale;
      } else if (selectedDiamondShape === 7) { 
        if(selectedDiamondSize === 0 )
          shapeOffset = -0.3 * diamondSizeScale;
        if(selectedDiamondSize === 5 )
          shapeOffset = 0.3 * diamondSizeScale;
      } else if (selectedDiamondShape === 8) { 
        shapeOffset = -0.3 * diamondSizeScale;
      } else if (selectedDiamondShape === 9) { 
        shapeOffset = -2.35 * diamondSizeScale;
        if(selectedDiamondSize === 0 )
          shapeOffset = -2.6 * diamondSizeScale;
      }
      
      basketBottomZ = box.max.z + specialOffset + shapeOffset;
    } else if (selectedSettingStyle === 2 && selectedBandStyle === 4) {
      // Special case: Classic Basket + Channel Set
      // Interpolate offset from -0.5*diamondSizeScale at 1ct to 0.5*diamondSizeScale at 4ct
      const t = selectedDiamondSize / 5; // 0 for 1ct, 1 for 4ct
      const specialOffset = ((1.0 * t) - 0.5) * diamondSizeScale;

      let shapeOffset = 0;
      if (selectedDiamondShape === 1) { // 1 = Princess
        shapeOffset = -0.5 * diamondSizeScale; // Tweak as needed
      } else if (selectedDiamondShape === 2) { 
        shapeOffset = -0.1 * diamondSizeScale; // 2 = Marquise
        if(selectedDiamondSize === 0 )
          shapeOffset = -0.3 * diamondSizeScale;
      } else if (selectedDiamondShape === 3) { 
        shapeOffset = -0.8 * diamondSizeScale;
      } else if (selectedDiamondShape === 4) { 
        shapeOffset = -2.3 * diamondSizeScale;
        if(selectedDiamondSize === 0 )
          shapeOffset = -2.5 * diamondSizeScale;
      } else if (selectedDiamondShape === 5) { 
        shapeOffset = -0.8 * diamondSizeScale;
      } else if (selectedDiamondShape === 6) { 
        shapeOffset = -2.3 * diamondSizeScale;
        if(selectedDiamondSize === 0 )
          shapeOffset = -2.5 * diamondSizeScale;
      } else if (selectedDiamondShape === 7) { 
        shapeOffset = -0.3 * diamondSizeScale;
        if(selectedDiamondSize === 0 )
          shapeOffset = -0.5 * diamondSizeScale;
        if(selectedDiamondSize === 5 )
          shapeOffset = -0.05 * diamondSizeScale;
      } else if (selectedDiamondShape === 8) { 
        shapeOffset = -0.3 * diamondSizeScale;
      } else if (selectedDiamondShape === 9) { 
        shapeOffset = -2.5 * diamondSizeScale;
      }

      basketBottomZ = box.max.z + specialOffset + shapeOffset;
    } else if ((selectedBandStyle === 0 || selectedBandStyle === 1 || selectedBandStyle === 2 || selectedBandStyle === 5)
      && selectedSettingStyle === 2) {
     const t = selectedDiamondSize / 5; // 0 for 1ct, 1 for 4ct
     const specialOffset = ((1.0 * t) + 12.5);
     let shapeOffset = 0;
     if (selectedDiamondShape === 0) {
      shapeOffset = -10.0;
      if(selectedDiamondSize === 0)
        shapeOffset = -10.2;
     }
     else if (selectedDiamondShape === 1) {
      shapeOffset = -10.8; // Tweak as needed
    }
    else if (selectedDiamondShape === 2) {
      shapeOffset = -10.0;
      if(selectedDiamondSize === 0)
        shapeOffset = -10.2;
    }  else if (selectedDiamondShape === 3) {
      shapeOffset = -10.8;
    } else if (selectedDiamondShape === 4 || selectedDiamondShape === 6 || selectedDiamondShape === 9) {
      shapeOffset = -13.6;
      if(selectedDiamondSize === 0)
        shapeOffset = -12.6;
      if(selectedDiamondSize === 1)
        shapeOffset = -12.8;
      if(selectedDiamondSize === 2)
        shapeOffset = -13.0;
      if(selectedDiamondSize === 3)
        shapeOffset = -13.2;
    }
     else if (selectedDiamondShape === 5) {
       shapeOffset = -10.8; // Tweak as needed
     } else if (selectedDiamondShape === 7) {
      shapeOffset = -10.4; // Tweak as needed
      if(selectedDiamondSize === 5)
        shapeOffset = -9.8; // Tweak as needed
    } else if (selectedDiamondShape === 8) {
      shapeOffset = -10.4; // Tweak as needed
    } 
     basketBottomZ = box.max.z + specialOffset + shapeOffset;

   } else if (selectedSettingStyle === 2) {
      // Classic Basket (other bands)
      let classicBasketOffset = 2.4 * diamondSizeScale;
      basketBottomZ = box.max.z + classicBasketOffset;
    } else if (selectedBandStyle === 3) {
      // Wide Solitaire - use standard positioning
      basketBottomZ = box.max.z;
    } else if (selectedBandStyle === 4) {
      // Channel Set (other settings)
      const channelSetOffset = -3.0;
      basketBottomZ = box.max.z + channelSetOffset;
    } else if (selectedSettingStyle === 7) {
      // Pear 3 Stone - special positioning to keep it vertically centered
      const pear3StoneOffset = -18.0 * diamondSizeScale; // Much larger offset to bring the ring down significantly
      basketBottomZ = box.max.z + pear3StoneOffset;
    } else {
      basketBottomZ = box.max.z;
    }
  }

  // 5. Magnet offset: align basket bottom to band top
  const magnetOffset = bandTopZ - basketBottomZ;

  return (
      <group position={[0, 0, 0]} rotation={rotation} scale={baseScale}>
        {/* Band parts - at base scale */}
        {/* Solitaire band style node */}
        {!showOnlyDiamond && selectedSettingStyle !== 7 && selectedSettingStyle !== 8 && selectedSettingStyle !== 9 && showSolitaireBand && showSolitaireBand.geometry && (
          <mesh geometry={showSolitaireBand.geometry.clone()} key={'3_Solitaire'} castShadow receiveShadow position={[0, 0, 0]} scale={[1.0, 1.0, 1.0]}>
            <meshPhysicalMaterial
              color={metals && typeof selectedBandMetal === 'number' ? metals[selectedBandMetal] : headProps.color}
              roughness={headProps.roughness}
              metalness={headProps.metalness}
              clearcoat={headProps.clearcoat}
              clearcoatRoughness={headProps.clearcoatRoughness}
              transmission={headProps.transmission}
              ior={headProps.ior}
              thickness={headProps.thickness}
              envMap={metalEnv}
              envMapIntensity={0.8}
              clipShadows
            />
          </mesh>
        )}
        {/* French Pave band style nodes */}
        {!showOnlyDiamond && selectedSettingStyle !== 7 && selectedSettingStyle !== 8 && selectedSettingStyle !== 9 && showFrenchPaveBand && (
          <>
            <mesh geometry={nodes['3_French_Pave'].geometry.clone()} key={'3_French_Pave'} castShadow receiveShadow position={[0, 0, 0]} scale={[1.0, 1.0, 1.0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedBandMetal === 'number' ? metals[selectedBandMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
            <mesh geometry={nodes['3_French_Pave_Diamonds'].geometry.clone()} key={'3_French_Pave_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} scale={[1.0, 1.0, 1.0]}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          </>
        )}
        {/* U Shaped Pave band style nodes */}
        {!showOnlyDiamond && selectedSettingStyle !== 7 && selectedSettingStyle !== 8 && selectedSettingStyle !== 9 && showUShapedPaveBand && (
          <>
            <mesh geometry={nodes['3_U_Shaped_Pave'].geometry.clone()} key={'3_U_Shaped_Pave'} castShadow receiveShadow position={[0, 0, 0]} scale={[1.0, 1.0, 1.0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedBandMetal === 'number' ? metals[selectedBandMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
            <mesh geometry={nodes['3_U_Shaped_Pave_Diamonds'].geometry.clone()} key={'3_U_Shaped_Pave_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} scale={[1.0, 1.0, 1.0]}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          </>
        )}
        {/* Channel Set band style node */}
        {!showOnlyDiamond && selectedSettingStyle !== 7 && selectedSettingStyle !== 8 && selectedSettingStyle !== 9 && showChannelSetBand && (
          <mesh geometry={nodes['3_Cathedral_Solitaire'].geometry.clone()} key={'3_Cathedral_Solitaire'} castShadow receiveShadow position={[0, 0, 0]} scale={[1.0, 1.0, 1.0]}>
            <meshPhysicalMaterial
              color={metals && typeof selectedBandMetal === 'number' ? metals[selectedBandMetal] : headProps.color}
              roughness={headProps.roughness}
              metalness={headProps.metalness}
              clearcoat={headProps.clearcoat}
              clearcoatRoughness={headProps.clearcoatRoughness}
              transmission={headProps.transmission}
              ior={headProps.ior}
              thickness={headProps.thickness}
              envMap={metalEnv}
              envMapIntensity={0.8}
              clipShadows
            />
          </mesh>
        )}
        {/* Wide Solitaire band style node */}
        {!showOnlyDiamond && selectedSettingStyle !== 7 && selectedSettingStyle !== 8 && selectedSettingStyle !== 9 && showWideSolitaireBand && (
          <mesh geometry={nodes['3_Wide_Solitaire'].geometry.clone()} key={'3_Wide_Solitaire'} castShadow receiveShadow position={[0, 0, 0]} scale={[1.0, 1.0, 1.0]}>
            <meshPhysicalMaterial
              color={metals && typeof selectedBandMetal === 'number' ? metals[selectedBandMetal] : headProps.color}
              roughness={headProps.roughness}
              metalness={headProps.metalness}
              clearcoat={headProps.clearcoat}
              clearcoatRoughness={headProps.clearcoatRoughness}
              transmission={headProps.transmission}
              ior={headProps.ior}
              thickness={headProps.thickness}
              envMap={metalEnv}
              envMapIntensity={0.8}
              clipShadows
            />
          </mesh>
        )}


        
        {/* Visualization dots - OUTSIDE scaled group so they're always visible */}
        {/* Red dot at diamond center - scaled and adjusted to match actual diamond position */}

        
        {/* Diamond and Setting parts - scaled based on diamond size, with Z position adjustment */}
        <group scale={[diamondSizeScale, diamondSizeScale, diamondSizeScale]} position={(selectedSettingStyle === 8 || selectedSettingStyle === 9) ? [0, 0, 0] : [0, 0, magnetOffset]}>
          {/* Diamond part */}
          {selectedSettingStyle !== 7 && diamondNode && diamondNode.geometry && typeof diamondProps.color === 'string' && (
            <mesh geometry={diamondNode.geometry.clone()} key={selectedNode + '-diamond'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Heart Diamonds mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 9 && nodes['2_Pear3Stone_Heart_Diamonds'] && nodes['2_Pear3Stone_Heart_Diamonds'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Heart_Diamonds'].geometry.clone()} key={'2_Pear3Stone_Heart_Diamonds'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Round mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 0 && nodes['2_Pear3Stone_Round'] && nodes['2_Pear3Stone_Round'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Round'].geometry.clone()} key={'2_Pear3Stone_Round'} castShadow receiveShadow>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Round Diamonds mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 0 && nodes['2_Pear3Stone_Round_Diamonds'] && nodes['2_Pear3Stone_Round_Diamonds'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Round_Diamonds'].geometry.clone()} key={'2_Pear3Stone_Round_Diamonds'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          {/* Four Prongs mesh */}
          {!showOnlyDiamond && selectedSettingStyle !== 7 && fourProngsMesh}
          {/* Six Prongs mesh */}
          {!showOnlyDiamond && selectedSettingStyle !== 7 && sixProngsMesh}
          {/* Classic Basket mesh */}
          {!showOnlyDiamond && selectedSettingStyle !== 7 && classicBasketMesh}
          {/* 4-P Bloom mesh */}
          {!showOnlyDiamond && selectedSettingStyle !== 7 && fourPBloomMesh}
          {/* 6-P Bloom mesh */}
          {!showOnlyDiamond && selectedSettingStyle !== 7 && sixPBloomMesh}
          {/* Pear 3 Stone + Heart mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 9 && pear3StoneHeartMesh}
          {/* Pave Basket nodes */}
          {!showOnlyDiamond && selectedSettingStyle === 4 && (
            <group position={[0, 0, 0]}>
              {paveBasketDiamonds}
              {paveBasketBasket}
            </group>
          )}
          {/* Pave Halo nodes for all shapes */}
          {!showOnlyDiamond && selectedSettingStyle === 3 && (
            <group position={[0, 0, 0]}>
              {diamondsNode && diamondsNode.geometry && (
                <mesh geometry={diamondsNode.geometry.clone()} key={diamondsNode.name} castShadow receiveShadow>
                  <MeshRefractionMaterial
                    envMap={diamondEnv}
                    bounces={diamondProps.bounces}
                    ior={diamondProps.ior}
                    fresnel={diamondProps.fresnel}
                    aberrationStrength={diamondProps.aberrationStrength}
                    color={diamondProps.color}
                    side={THREE.DoubleSide}
                    toneMapped={true}
                    envMapIntensity={1.5}
                    {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                    clipShadows
                  />
                </mesh>
              )}
              {basketNode && basketNode.geometry && (
                <mesh geometry={basketNode.geometry.clone()} key={basketNode.name} castShadow receiveShadow>
                  <meshPhysicalMaterial
                    color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
                    roughness={headProps.roughness}
                    metalness={headProps.metalness}
                    clearcoat={headProps.clearcoat}
                    clearcoatRoughness={headProps.clearcoatRoughness}
                    transmission={headProps.transmission}
                    ior={headProps.ior}
                    thickness={headProps.thickness}
                    envMap={metalEnv}
                    envMapIntensity={0.8}
                    clipShadows
                  />
                </mesh>
              )}
            </group>
          )}
          {/* Pear 3 Stone + Asscher mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 7 && nodes['2_Pear3Stone_Asscher'] && nodes['2_Pear3Stone_Asscher'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Asscher'].geometry.clone()} key={'2_Pear3Stone_Asscher'} castShadow receiveShadow>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Asscher Diamonds mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 7 && nodes['2_Pear3Stone_Asscher_Diamonds'] && nodes['2_Pear3Stone_Asscher_Diamonds'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Asscher_Diamonds'].geometry.clone()} key={'2_Pear3Stone_Asscher_Diamonds'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Cushion mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 5 && nodes['2_Pear3Stone_Cushion'] && nodes['2_Pear3Stone_Cushion'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Cushion'].geometry.clone()} key={'2_Pear3Stone_Cushion'} castShadow receiveShadow>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Cushion Diamonds mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 5 && nodes['2_Pear3Stone_Cushion_Diamonds'] && nodes['2_Pear3Stone_Cushion_Diamonds'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Cushion_Diamonds'].geometry.clone()} key={'2_Pear3Stone_Cushion_Diamonds'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Emerald mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 3 && nodes['2_Pear3Stone_Emerald'] && nodes['2_Pear3Stone_Emerald'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Emerald'].geometry.clone()} key={'2_Pear3Stone_Emerald'} castShadow receiveShadow>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Emerald Diamonds mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 3 && nodes['2_Pear3Stone_Emerald_Diamonds'] && nodes['2_Pear3Stone_Emerald_Diamonds'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Emerald_Diamonds'].geometry.clone()} key={'2_Pear3Stone_Emerald_Diamonds'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Marquise mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 6 && nodes['2_Pear3Stone_Marquise'] && nodes['2_Pear3Stone_Marquise'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Marquise'].geometry.clone()} key={'2_Pear3Stone_Marquise'} castShadow receiveShadow>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Marquise Diamonds mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 6 && nodes['2_Pear3Stone_Marquise_Diamonds'] && nodes['2_Pear3Stone_Marquise_Diamonds'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Marquise_Diamonds'].geometry.clone()} key={'2_Pear3Stone_Marquise_Diamonds'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Oval mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 2 && nodes['2_Pear3Stone_Oval'] && nodes['2_Pear3Stone_Oval'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Oval'].geometry.clone()} key={'2_Pear3Stone_Oval'} castShadow receiveShadow>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Oval Diamonds mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 2 && nodes['2_Pear3Stone_Oval_Diamonds'] && nodes['2_Pear3Stone_Oval_Diamonds'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Oval_Diamonds'].geometry.clone()} key={'2_Pear3Stone_Oval_Diamonds'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Pear mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 4 && nodes['2_Pear3Stone_Pear'] && nodes['2_Pear3Stone_Pear'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Pear'].geometry.clone()} key={'2_Pear3Stone_Pear'} castShadow receiveShadow>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Pear Diamonds mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 4 && nodes['2_Pear3Stone_Pear_Diamonds'] && nodes['2_Pear3Stone_Pear_Diamonds'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Pear_Diamonds'].geometry.clone()} key={'2_Pear3Stone_Pear_Diamonds'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Princess mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 1 && nodes['2_Pear3Stone_Princess'] && nodes['2_Pear3Stone_Princess'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Princess'].geometry.clone()} key={'2_Pear3Stone_Princess'} castShadow receiveShadow>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Princess Diamonds mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 1 && nodes['2_Pear3Stone_Princess_Diamonds'] && nodes['2_Pear3Stone_Princess_Diamonds'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Princess_Diamonds'].geometry.clone()} key={'2_Pear3Stone_Princess_Diamonds'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Radiant mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 8 && nodes['2_Pear3Stone_Radiant'] && nodes['2_Pear3Stone_Radiant'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Radiant'].geometry.clone()} key={'2_Pear3Stone_Radiant'} castShadow receiveShadow>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : headProps.color}
                roughness={headProps.roughness}
                metalness={headProps.metalness}
                clearcoat={headProps.clearcoat}
                clearcoatRoughness={headProps.clearcoatRoughness}
                transmission={headProps.transmission}
                ior={headProps.ior}
                thickness={headProps.thickness}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          {/* Pear 3 Stone + Radiant Diamonds mesh */}
          {!showOnlyDiamond && selectedSettingStyle === 7 && selectedDiamondShape === 8 && nodes['2_Pear3Stone_Radiant_Diamonds'] && nodes['2_Pear3Stone_Radiant_Diamonds'].geometry && (
            <mesh geometry={nodes['2_Pear3Stone_Radiant_Diamonds'].geometry.clone()} key={'2_Pear3Stone_Radiant_Diamonds'} castShadow receiveShadow layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Asscher mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 7 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_Cathedral_Solitaire_ClassicBasket_Asscher'] && nodes['2_Cathedral_Solitaire_ClassicBasket_Asscher'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_ClassicBasket_Asscher'].geometry.clone()} key={'2_Cathedral_Solitaire_ClassicBasket_Asscher'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Asscher'] && nodes['2_ClassicBasket_Asscher'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Asscher'].geometry.clone()} key={'2_ClassicBasket_Asscher'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral + Asscher + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Asscher_Diamonds'] && nodes['2_ClassicBasket_Asscher_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Asscher_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Asscher_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral + Asscher + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Asscher'] && nodes['2_4P_Asscher'].geometry && (
            <mesh geometry={nodes['2_4P_Asscher'].geometry.clone()} key={'2_4P_Asscher_Cathedral'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Asscher'] && nodes['2_PaveBasket_Asscher'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Asscher'].geometry.clone()} key={'2_PaveBasket_Asscher'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral + Asscher + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Asscher_Diamonds'] && nodes['2_PaveBasket_Asscher_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Asscher_Diamonds'].geometry.clone()} key={'2_PaveBasket_Asscher_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Asscher mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_Cathedral_Solitaire_PaveHalo_Asscher'] && nodes['2_Cathedral_Solitaire_PaveHalo_Asscher'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_PaveHalo_Asscher'].geometry.clone()} key={'2_Cathedral_Solitaire_PaveHalo_Asscher'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Asscher'] && nodes['2_PaveHalo_Asscher'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Asscher'].geometry.clone()} key={'2_PaveHalo_Asscher'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral + Asscher + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Asscher_Diamonds'] && nodes['2_PaveHalo_Asscher_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Asscher_Diamonds'].geometry.clone()} key={'2_PaveHalo_Asscher_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Round mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 0 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_Cathedral_Solitaire_ClassicBasket_Round'] && nodes['2_Cathedral_Solitaire_ClassicBasket_Round'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_ClassicBasket_Round'].geometry.clone()} key={'2_Cathedral_Solitaire_ClassicBasket_Round'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Round mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_Cathedral_Solitaire_PaveHalo_Round'] && nodes['2_Cathedral_Solitaire_PaveHalo_Round'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_PaveHalo_Round'].geometry.clone()} key={'2_Cathedral_Solitaire_PaveHalo_Round'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Princess mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 1 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_Cathedral_Solitaire_ClassicBasket_Princess'] && nodes['2_Cathedral_Solitaire_ClassicBasket_Princess'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_ClassicBasket_Princess'].geometry.clone()} key={'2_Cathedral_Solitaire_ClassicBasket_Princess'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Princess mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_Cathedral_Solitaire_PaveHalo_Princess'] && nodes['2_Cathedral_Solitaire_PaveHalo_Princess'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_PaveHalo_Princess'].geometry.clone()} key={'2_Cathedral_Solitaire_PaveHalo_Princess'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Oval mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 2 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_Cathedral_Solitaire_ClassicBasket_Oval'] && nodes['2_Cathedral_Solitaire_ClassicBasket_Oval'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_ClassicBasket_Oval'].geometry.clone()} key={'2_Cathedral_Solitaire_ClassicBasket_Oval'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Oval mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_Cathedral_Solitaire_PaveHalo_Oval'] && nodes['2_Cathedral_Solitaire_PaveHalo_Oval'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_PaveHalo_Oval'].geometry.clone()} key={'2_Cathedral_Solitaire_PaveHalo_Oval'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Emerald mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 3 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_Cathedral_Solitaire_ClassicBasket_Emerald'] && nodes['2_Cathedral_Solitaire_ClassicBasket_Emerald'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_ClassicBasket_Emerald'].geometry.clone()} key={'2_Cathedral_Solitaire_ClassicBasket_Emerald'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Emerald mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_Cathedral_Solitaire_PaveHalo_Emerald'] && nodes['2_Cathedral_Solitaire_PaveHalo_Emerald'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_PaveHalo_Emerald'].geometry.clone()} key={'2_Cathedral_Solitaire_PaveHalo_Emerald'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Pear mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 4 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_Cathedral_Solitaire_ClassicBasket_Pear'] && nodes['2_Cathedral_Solitaire_ClassicBasket_Pear'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_ClassicBasket_Pear'].geometry.clone()} key={'2_Cathedral_Solitaire_ClassicBasket_Pear'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Pear mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_Cathedral_Solitaire_PaveHalo_Pear'] && nodes['2_Cathedral_Solitaire_PaveHalo_Pear'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_PaveHalo_Pear'].geometry.clone()} key={'2_Cathedral_Solitaire_PaveHalo_Pear'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Cushion mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 5 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_Cathedral_Solitaire_ClassicBasket_Cushion'] && nodes['2_Cathedral_Solitaire_ClassicBasket_Cushion'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_ClassicBasket_Cushion'].geometry.clone()} key={'2_Cathedral_Solitaire_ClassicBasket_Cushion'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Cushion mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_Cathedral_Solitaire_PaveHalo_Cushion'] && nodes['2_Cathedral_Solitaire_PaveHalo_Cushion'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_PaveHalo_Cushion'].geometry.clone()} key={'2_Cathedral_Solitaire_PaveHalo_Cushion'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Marquise mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 6 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_Cathedral_Solitaire_ClassicBasket_Marquise'] && nodes['2_Cathedral_Solitaire_ClassicBasket_Marquise'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_ClassicBasket_Marquise'].geometry.clone()} key={'2_Cathedral_Solitaire_ClassicBasket_Marquise'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Marquise mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_Cathedral_Solitaire_PaveHalo_Marquise'] && nodes['2_Cathedral_Solitaire_PaveHalo_Marquise'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_PaveHalo_Marquise'].geometry.clone()} key={'2_Cathedral_Solitaire_PaveHalo_Marquise'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Radiant mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 8 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_Cathedral_Solitaire_ClassicBasket_Radiant'] && nodes['2_Cathedral_Solitaire_ClassicBasket_Radiant'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_ClassicBasket_Radiant'].geometry.clone()} key={'2_Cathedral_Solitaire_ClassicBasket_Radiant'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Radiant mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_Cathedral_Solitaire_PaveHalo_Radiant'] && nodes['2_Cathedral_Solitaire_PaveHalo_Radiant'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_PaveHalo_Radiant'].geometry.clone()} key={'2_Cathedral_Solitaire_PaveHalo_Radiant'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Heart mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 9 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_Cathedral_Solitaire_ClassicBasket_Heart'] && nodes['2_Cathedral_Solitaire_ClassicBasket_Heart'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_ClassicBasket_Heart'].geometry.clone()} key={'2_Cathedral_Solitaire_ClassicBasket_Heart'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral + Solitaire + Heart mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_Cathedral_Solitaire_PaveHalo_Heart'] && nodes['2_Cathedral_Solitaire_PaveHalo_Heart'].geometry && (
            <mesh geometry={nodes['2_Cathedral_Solitaire_PaveHalo_Heart'].geometry.clone()} key={'2_Cathedral_Solitaire_PaveHalo_Heart'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Round mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Round'] && nodes['2_CathedralPave_ClassicBasket_Round'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Round'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Round'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Round mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Round'] && nodes['2_CathedralPave_PaveHalo_Round'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Round'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Round'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Round + Classic Basket or Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Round_Diamonds'] && nodes['2_CathedralPave_ClassicBasket_Round_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Round_Diamonds'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Round_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Round + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Round_Diamonds'] && nodes['2_CathedralPave_PaveHalo_Round_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Round_Diamonds'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Round_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Princess mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Princess'] && nodes['2_CathedralPave_ClassicBasket_Princess'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Princess'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Princess'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Princess mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Princess'] && nodes['2_CathedralPave_PaveHalo_Princess'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Princess'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Princess'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Princess + Classic Basket or Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Princess_Diamonds'] && nodes['2_CathedralPave_ClassicBasket_Princess_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Princess_Diamonds'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Princess_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Princess + Pavé Halo annotation */}
          {(() => {
            const shouldRender = selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-halo';
            const meshExists = nodes['2_CathedralPave_PaveHalo_Princess_Daimonds'] && nodes['2_CathedralPave_PaveHalo_Princess_Daimonds'].geometry;
            
            if (shouldRender) {
              console.log('Cathedral Pavé Princess Pavé Halo Debug:', {
                selectedSettingStyle,
                selectedBandStyle,
                selectedDiamondShape,
                selectedCathedralAnnotation,
                meshExists,
                meshNode: nodes['2_CathedralPave_PaveHalo_Princess_Daimonds']
              });
              
              // List all available Cathedral Pavé Princess meshes
              const availableMeshes = Object.keys(nodes).filter(key => 
                key.includes('CathedralPave') && key.includes('Princess')
              );
              console.log('Available Cathedral Pavé Princess meshes:', availableMeshes);
            }
            
            return shouldRender && meshExists;
          })() && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Princess_Daimonds'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Princess_Daimonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Oval mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Oval'] && nodes['2_CathedralPave_ClassicBasket_Oval'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Oval'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Oval'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Oval mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Oval'] && nodes['2_CathedralPave_PaveHalo_Oval'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Oval'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Oval'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Oval + Classic Basket or Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Oval_Diamonds'] && nodes['2_CathedralPave_ClassicBasket_Oval_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Oval_Diamonds'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Oval_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Oval + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Oval_Diamonds'] && nodes['2_CathedralPave_PaveHalo_Oval_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Oval_Diamonds'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Oval_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Emerald mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Emerald'] && nodes['2_CathedralPave_ClassicBasket_Emerald'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Emerald'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Emerald'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Emerald mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Emerald'] && nodes['2_CathedralPave_PaveHalo_Emerald'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Emerald'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Emerald'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Emerald + Classic Basket or Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Emerald_Diamonds'] && nodes['2_CathedralPave_ClassicBasket_Emerald_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Emerald_Diamonds'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Emerald_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Emerald + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Emerald_Diamonds'] && nodes['2_CathedralPave_PaveHalo_Emerald_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Emerald_Diamonds'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Emerald_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Pear mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 4 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Pear'] && nodes['2_CathedralPave_ClassicBasket_Pear'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Pear'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Pear'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Pear mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Pear'] && nodes['2_CathedralPave_PaveHalo_Pear'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Pear'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Pear'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Pear + Classic Basket or Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 4 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Pear_Diamonds'] && nodes['2_CathedralPave_ClassicBasket_Pear_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Pear_Diamonds'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Pear_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Pear + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Pear_Diamonds'] && nodes['2_CathedralPave_PaveHalo_Pear_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Pear_Diamonds'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Pear_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Cushion mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Cushion'] && nodes['2_CathedralPave_ClassicBasket_Cushion'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Cushion'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Cushion'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Cushion mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Cushion'] && nodes['2_CathedralPave_PaveHalo_Cushion'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Cushion'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Cushion'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Cushion + Classic Basket or Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Cushion_Diamonds'] && nodes['2_CathedralPave_ClassicBasket_Cushion_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Cushion_Diamonds'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Cushion_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Cushion + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Cushion_Diamonds'] && nodes['2_CathedralPave_PaveHalo_Cushion_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Cushion_Diamonds'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Cushion_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Marquise mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 6 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Marquise'] && nodes['2_CathedralPave_ClassicBasket_Marquise'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Marquise'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Marquise'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Marquise mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Marquise'] && nodes['2_CathedralPave_PaveHalo_Marquise'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Marquise'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Marquise'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Marquise + Classic Basket or Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 6 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Marquise_Diamonds'] && nodes['2_CathedralPave_ClassicBasket_Marquise_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Marquise_Diamonds'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Marquise_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Marquise + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Marquise_Diamonds'] && nodes['2_CathedralPave_PaveHalo_Marquise_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Marquise_Diamonds'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Marquise_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Asscher mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Asscher'] && nodes['2_CathedralPave_ClassicBasket_Asscher'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Asscher'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Asscher'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Asscher mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Asscher'] && nodes['2_CathedralPave_PaveHalo_Asscher'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Asscher'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Asscher'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Asscher + Classic Basket or Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Asscher_Diamonds'] && nodes['2_CathedralPave_ClassicBasket_Asscher_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Asscher_Diamonds'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Asscher_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Asscher + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Asscher_Diamonds'] && nodes['2_CathedralPave_PaveHalo_Asscher_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Asscher_Diamonds'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Asscher_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Radiant mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Radiant'] && nodes['2_CathedralPave_ClassicBasket_Radiant'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Radiant'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Radiant'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Radiant mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Radiant'] && nodes['2_CathedralPave_PaveHalo_Radiant'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Radiant'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Radiant'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Radiant + Classic Basket or Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Radiant_Diamonds'] && nodes['2_CathedralPave_ClassicBasket_Radiant_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Radiant_Diamonds'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Radiant_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Radiant + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Radiant_Diamonds'] && nodes['2_CathedralPave_PaveHalo_Radiant_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Radiant_Diamonds'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Radiant_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Heart mesh - Classic Basket or Pavé Basket */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 9 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Heart'] && nodes['2_CathedralPave_ClassicBasket_Heart'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Heart'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Heart'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé + Heart mesh - Pavé Halo */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Heart'] && nodes['2_CathedralPave_PaveHalo_Heart'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Heart'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Heart'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Heart + Classic Basket or Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 9 && (selectedCathedralAnnotation === 'classic-basket' || selectedCathedralAnnotation === 'pave-basket') && nodes['2_CathedralPave_ClassicBasket_Heart_Diamonds'] && nodes['2_CathedralPave_ClassicBasket_Heart_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_ClassicBasket_Heart_Diamonds'].geometry.clone()} key={'2_CathedralPave_ClassicBasket_Heart_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Cathedral Pavé Diamonds mesh for Heart + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_CathedralPave_PaveHalo_Heart_Diamonds'] && nodes['2_CathedralPave_PaveHalo_Heart_Diamonds'].geometry && (
            <mesh geometry={nodes['2_CathedralPave_PaveHalo_Heart_Diamonds'].geometry.clone()} key={'2_CathedralPave_PaveHalo_Heart_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral + Round + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Round'] && nodes['2_ClassicBasket_Round'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Round'].geometry.clone()} key={'2_ClassicBasket_Round'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral + Round + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Round'] && nodes['2_4P_Round'].geometry && (
            <mesh geometry={nodes['2_4P_Round'].geometry.clone()} key={'2_4P_Round_Cathedral'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral + Round + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Round'] && nodes['2_PaveBasket_Round'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Round'].geometry.clone()} key={'2_PaveBasket_Round'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral + Round + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Round_Diamonds'] && nodes['2_PaveBasket_Round_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Round_Diamonds'].geometry.clone()} key={'2_PaveBasket_Round_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral + Round + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Round'] && nodes['2_PaveHalo_Round'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Round'].geometry.clone()} key={'2_PaveHalo_Round'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral + Round + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Round_Diamonds'] && nodes['2_PaveHalo_Round_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Round_Diamonds'].geometry.clone()} key={'2_PaveHalo_Round_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral + Princess + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Princess'] && nodes['2_ClassicBasket_Princess'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Princess'].geometry.clone()} key={'2_ClassicBasket_Princess'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral + Princess + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Princess_Diamonds'] && nodes['2_ClassicBasket_Princess_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Princess_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Princess_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral + Princess + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Princess'] && nodes['2_4P_Princess'].geometry && (
            <mesh geometry={nodes['2_4P_Princess'].geometry.clone()} key={'2_4P_Princess_Cathedral'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral + Princess + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Princess'] && nodes['2_PaveBasket_Princess'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Princess'].geometry.clone()} key={'2_PaveBasket_Princess'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral + Princess + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Princess_Diamonds'] && nodes['2_PaveBasket_Princess_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Princess_Diamonds'].geometry.clone()} key={'2_PaveBasket_Princess_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral + Princess + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Princess'] && nodes['2_PaveHalo_Princess'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Princess'].geometry.clone()} key={'2_PaveHalo_Princess'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral + Princess + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Princess_Diamonds'] && nodes['2_PaveHalo_Princess_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Princess_Diamonds'].geometry.clone()} key={'2_PaveHalo_Princess_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral + Oval + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Oval'] && nodes['2_ClassicBasket_Oval'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Oval'].geometry.clone()} key={'2_ClassicBasket_Oval'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral + Oval + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Oval_Diamonds'] && nodes['2_ClassicBasket_Oval_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Oval_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Oval_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral + Oval + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Oval'] && nodes['2_4P_Oval'].geometry && (
            <mesh geometry={nodes['2_4P_Oval'].geometry.clone()} key={'2_4P_Oval_Cathedral'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral + Oval + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Oval'] && nodes['2_PaveBasket_Oval'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Oval'].geometry.clone()} key={'2_PaveBasket_Oval'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral + Oval + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Oval_Diamonds'] && nodes['2_PaveBasket_Oval_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Oval_Diamonds'].geometry.clone()} key={'2_PaveBasket_Oval_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral + Oval + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Oval'] && nodes['2_PaveHalo_Oval'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Oval'].geometry.clone()} key={'2_PaveHalo_Oval'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral + Oval + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Oval_Diamonds'] && nodes['2_PaveHalo_Oval_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Oval_Diamonds'].geometry.clone()} key={'2_PaveHalo_Oval_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral + Emerald + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Emerald'] && nodes['2_ClassicBasket_Emerald'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Emerald'].geometry.clone()} key={'2_ClassicBasket_Emerald'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral + Emerald + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Emerald_Diamonds'] && nodes['2_ClassicBasket_Emerald_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Emerald_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Emerald_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral + Emerald + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Emerald'] && nodes['2_4P_Emerald'].geometry && (
            <mesh geometry={nodes['2_4P_Emerald'].geometry.clone()} key={'2_4P_Emerald_Cathedral'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral + Emerald + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Emerald'] && nodes['2_PaveBasket_Emerald'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Emerald'].geometry.clone()} key={'2_PaveBasket_Emerald'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral + Emerald + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Emerald_Diamonds'] && nodes['2_PaveBasket_Emerald_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Emerald_Diamonds'].geometry.clone()} key={'2_PaveBasket_Emerald_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral + Emerald + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Emerald'] && nodes['2_PaveHalo_Emerald'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Emerald'].geometry.clone()} key={'2_PaveHalo_Emerald'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral + Emerald + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Emerald_Diamonds'] && nodes['2_PaveHalo_Emerald_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Emerald_Diamonds'].geometry.clone()} key={'2_PaveHalo_Emerald_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral + Pear + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Pear'] && nodes['2_ClassicBasket_Pear'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Pear'].geometry.clone()} key={'2_ClassicBasket_Pear'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral + Pear + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Pear_Diamonds'] && nodes['2_ClassicBasket_Pear_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Pear_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Pear_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral + Pear + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Pear'] && nodes['2_PaveBasket_Pear'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Pear'].geometry.clone()} key={'2_PaveBasket_Pear'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral + Pear + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Pear_Diamonds'] && nodes['2_PaveBasket_Pear_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Pear_Diamonds'].geometry.clone()} key={'2_PaveBasket_Pear_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral + Pear + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Pear'] && nodes['2_PaveHalo_Pear'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Pear'].geometry.clone()} key={'2_PaveHalo_Pear'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral + Pear + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Pear_Diamonds'] && nodes['2_PaveHalo_Pear_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Pear_Diamonds'].geometry.clone()} key={'2_PaveHalo_Pear_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral + Cushion + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Cushion'] && nodes['2_ClassicBasket_Cushion'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Cushion'].geometry.clone()} key={'2_ClassicBasket_Cushion'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral + Cushion + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Cushion_Diamonds'] && nodes['2_ClassicBasket_Cushion_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Cushion_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Cushion_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral + Cushion + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Cushion'] && nodes['2_4P_Cushion'].geometry && (
            <mesh geometry={nodes['2_4P_Cushion'].geometry.clone()} key={'2_4P_Cushion_Cathedral'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral + Cushion + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Cushion'] && nodes['2_PaveBasket_Cushion'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Cushion'].geometry.clone()} key={'2_PaveBasket_Cushion'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral + Cushion + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Cushion_Diamonds'] && nodes['2_PaveBasket_Cushion_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Cushion_Diamonds'].geometry.clone()} key={'2_PaveBasket_Cushion_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral + Cushion + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Cushion'] && nodes['2_PaveHalo_Cushion'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Cushion'].geometry.clone()} key={'2_PaveHalo_Cushion'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral + Cushion + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Cushion_Diamonds'] && nodes['2_PaveHalo_Cushion_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Cushion_Diamonds'].geometry.clone()} key={'2_PaveHalo_Cushion_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral + Marquise + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Marquise'] && nodes['2_ClassicBasket_Marquise'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Marquise'].geometry.clone()} key={'2_ClassicBasket_Marquise'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral + Marquise + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Marquise_Diamonds'] && nodes['2_ClassicBasket_Marquise_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Marquise_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Marquise_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral + Marquise + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Marquise'] && nodes['2_PaveBasket_Marquise'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Marquise'].geometry.clone()} key={'2_PaveBasket_Marquise'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral + Marquise + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Marquise_Diamonds'] && nodes['2_PaveBasket_Marquise_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Marquise_Diamonds'].geometry.clone()} key={'2_PaveBasket_Marquise_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral + Marquise + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Marquise'] && nodes['2_PaveHalo_Marquise'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Marquise'].geometry.clone()} key={'2_PaveHalo_Marquise'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral + Marquise + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Marquise_Diamonds'] && nodes['2_PaveHalo_Marquise_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Marquise_Diamonds'].geometry.clone()} key={'2_PaveHalo_Marquise_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral + Radiant + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Radiant'] && nodes['2_ClassicBasket_Radiant'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Radiant'].geometry.clone()} key={'2_ClassicBasket_Radiant'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral + Radiant + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Radiant_Diamonds'] && nodes['2_ClassicBasket_Radiant_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Radiant_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Radiant_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral + Radiant + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Radiant'] && nodes['2_4P_Radiant'].geometry && (
            <mesh geometry={nodes['2_4P_Radiant'].geometry.clone()} key={'2_4P_Radiant_Cathedral'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral + Radiant + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Radiant'] && nodes['2_PaveBasket_Radiant'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Radiant'].geometry.clone()} key={'2_PaveBasket_Radiant'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral + Radiant + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Radiant_Diamonds'] && nodes['2_PaveBasket_Radiant_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Radiant_Diamonds'].geometry.clone()} key={'2_PaveBasket_Radiant_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral + Radiant + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Radiant'] && nodes['2_PaveHalo_Radiant'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Radiant'].geometry.clone()} key={'2_PaveHalo_Radiant'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral + Radiant + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Radiant_Diamonds'] && nodes['2_PaveHalo_Radiant_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Radiant_Diamonds'].geometry.clone()} key={'2_PaveHalo_Radiant_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral + Heart + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Heart'] && nodes['2_ClassicBasket_Heart'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Heart'].geometry.clone()} key={'2_ClassicBasket_Heart'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral + Heart + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Heart_Diamonds'] && nodes['2_ClassicBasket_Heart_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Heart_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Heart_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral + Heart + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Heart'] && nodes['2_PaveBasket_Heart'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Heart'].geometry.clone()} key={'2_PaveBasket_Heart'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral + Heart + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Heart_Diamonds'] && nodes['2_PaveBasket_Heart_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Heart_Diamonds'].geometry.clone()} key={'2_PaveBasket_Heart_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral + Heart + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Heart'] && nodes['2_PaveHalo_Heart'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Heart'].geometry.clone()} key={'2_PaveHalo_Heart'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral + Heart + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 8 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Heart_Diamonds'] && nodes['2_PaveHalo_Heart_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Heart_Diamonds'].geometry.clone()} key={'2_PaveHalo_Heart_Diamonds'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral Pavé + Round + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Round'] && nodes['2_ClassicBasket_Round'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Round'].geometry.clone()} key={'2_ClassicBasket_Round_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral Pavé + Round + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Round_Diamonds'] && nodes['2_ClassicBasket_Round_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Round_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Round_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral Pavé + Round + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Round'] && nodes['2_4P_Round'].geometry && (
            <mesh geometry={nodes['2_4P_Round'].geometry.clone()} key={'2_4P_Round_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral Pavé + Round + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Round'] && nodes['2_PaveBasket_Round'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Round'].geometry.clone()} key={'2_PaveBasket_Round_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral Pavé + Round + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Round_Diamonds'] && nodes['2_PaveBasket_Round_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Round_Diamonds'].geometry.clone()} key={'2_PaveBasket_Round_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral Pavé + Round + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Round'] && nodes['2_PaveHalo_Round'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Round'].geometry.clone()} key={'2_PaveHalo_Round_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral Pavé + Round + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 0 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Round_Diamonds'] && nodes['2_PaveHalo_Round_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Round_Diamonds'].geometry.clone()} key={'2_PaveHalo_Round_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral Pavé + Princess + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Princess'] && nodes['2_ClassicBasket_Princess'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Princess'].geometry.clone()} key={'2_ClassicBasket_Princess_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral Pavé + Princess + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Princess_Diamonds'] && nodes['2_ClassicBasket_Princess_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Princess_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Princess_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral Pavé + Princess + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Princess'] && nodes['2_4P_Princess'].geometry && (
            <mesh geometry={nodes['2_4P_Princess'].geometry.clone()} key={'2_4P_Princess_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral Pavé + Princess + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Princess'] && nodes['2_PaveBasket_Princess'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Princess'].geometry.clone()} key={'2_PaveBasket_Princess_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral Pavé + Princess + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Princess_Diamonds'] && nodes['2_PaveBasket_Princess_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Princess_Diamonds'].geometry.clone()} key={'2_PaveBasket_Princess_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral Pavé + Princess + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Princess'] && nodes['2_PaveHalo_Princess'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Princess'].geometry.clone()} key={'2_PaveHalo_Princess_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral Pavé + Princess + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 1 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Princess_Diamonds'] && nodes['2_PaveHalo_Princess_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Princess_Diamonds'].geometry.clone()} key={'2_PaveHalo_Princess_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral Pavé + Oval + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Oval'] && nodes['2_ClassicBasket_Oval'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Oval'].geometry.clone()} key={'2_ClassicBasket_Oval_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral Pavé + Oval + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Oval_Diamonds'] && nodes['2_ClassicBasket_Oval_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Oval_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Oval_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral Pavé + Oval + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Oval'] && nodes['2_4P_Oval'].geometry && (
            <mesh geometry={nodes['2_4P_Oval'].geometry.clone()} key={'2_4P_Oval_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral Pavé + Oval + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Oval'] && nodes['2_PaveBasket_Oval'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Oval'].geometry.clone()} key={'2_PaveBasket_Oval_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral Pavé + Oval + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Oval_Diamonds'] && nodes['2_PaveBasket_Oval_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Oval_Diamonds'].geometry.clone()} key={'2_PaveBasket_Oval_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral Pavé + Oval + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Oval'] && nodes['2_PaveHalo_Oval'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Oval'].geometry.clone()} key={'2_PaveHalo_Oval_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral Pavé + Oval + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 2 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Oval_Diamonds'] && nodes['2_PaveHalo_Oval_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Oval_Diamonds'].geometry.clone()} key={'2_PaveHalo_Oval_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral Pavé + Emerald + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Emerald'] && nodes['2_ClassicBasket_Emerald'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Emerald'].geometry.clone()} key={'2_ClassicBasket_Emerald_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral Pavé + Emerald + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Emerald_Diamonds'] && nodes['2_ClassicBasket_Emerald_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Emerald_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Emerald_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral Pavé + Emerald + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Emerald'] && nodes['2_4P_Emerald'].geometry && (
            <mesh geometry={nodes['2_4P_Emerald'].geometry.clone()} key={'2_4P_Emerald_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral Pavé + Emerald + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Emerald'] && nodes['2_PaveBasket_Emerald'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Emerald'].geometry.clone()} key={'2_PaveBasket_Emerald_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral Pavé + Emerald + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Emerald_Diamonds'] && nodes['2_PaveBasket_Emerald_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Emerald_Diamonds'].geometry.clone()} key={'2_PaveBasket_Emerald_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral Pavé + Emerald + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Emerald'] && nodes['2_PaveHalo_Emerald'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Emerald'].geometry.clone()} key={'2_PaveHalo_Emerald_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral Pavé + Emerald + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 3 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Emerald_Diamonds'] && nodes['2_PaveHalo_Emerald_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Emerald_Diamonds'].geometry.clone()} key={'2_PaveHalo_Emerald_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral Pavé + Pear + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Pear'] && nodes['2_ClassicBasket_Pear'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Pear'].geometry.clone()} key={'2_ClassicBasket_Pear_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral Pavé + Pear + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Pear_Diamonds'] && nodes['2_ClassicBasket_Pear_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Pear_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Pear_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral Pavé + Pear + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Pear'] && nodes['2_PaveBasket_Pear'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Pear'].geometry.clone()} key={'2_PaveBasket_Pear_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral Pavé + Pear + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Pear_Diamonds'] && nodes['2_PaveBasket_Pear_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Pear_Diamonds'].geometry.clone()} key={'2_PaveBasket_Pear_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral Pavé + Pear + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Pear'] && nodes['2_PaveHalo_Pear'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Pear'].geometry.clone()} key={'2_PaveHalo_Pear_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral Pavé + Pear + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 4 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Pear_Diamonds'] && nodes['2_PaveHalo_Pear_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Pear_Diamonds'].geometry.clone()} key={'2_PaveHalo_Pear_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral Pavé + Cushion + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Cushion'] && nodes['2_ClassicBasket_Cushion'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Cushion'].geometry.clone()} key={'2_ClassicBasket_Cushion_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral Pavé + Cushion + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Cushion_Diamonds'] && nodes['2_ClassicBasket_Cushion_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Cushion_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Cushion_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral Pavé + Cushion + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Cushion'] && nodes['2_4P_Cushion'].geometry && (
            <mesh geometry={nodes['2_4P_Cushion'].geometry.clone()} key={'2_4P_Cushion_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral Pavé + Cushion + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Cushion'] && nodes['2_PaveBasket_Cushion'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Cushion'].geometry.clone()} key={'2_PaveBasket_Cushion_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral Pavé + Cushion + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Cushion_Diamonds'] && nodes['2_PaveBasket_Cushion_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Cushion_Diamonds'].geometry.clone()} key={'2_PaveBasket_Cushion_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral Pavé + Cushion + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Cushion'] && nodes['2_PaveHalo_Cushion'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Cushion'].geometry.clone()} key={'2_PaveHalo_Cushion_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral Pavé + Cushion + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 5 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Cushion_Diamonds'] && nodes['2_PaveHalo_Cushion_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Cushion_Diamonds'].geometry.clone()} key={'2_PaveHalo_Cushion_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral Pavé + Marquise + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Marquise'] && nodes['2_ClassicBasket_Marquise'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Marquise'].geometry.clone()} key={'2_ClassicBasket_Marquise_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral Pavé + Marquise + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Marquise_Diamonds'] && nodes['2_ClassicBasket_Marquise_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Marquise_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Marquise_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral Pavé + Marquise + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Marquise'] && nodes['2_PaveBasket_Marquise'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Marquise'].geometry.clone()} key={'2_PaveBasket_Marquise_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral Pavé + Marquise + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Marquise_Diamonds'] && nodes['2_PaveBasket_Marquise_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Marquise_Diamonds'].geometry.clone()} key={'2_PaveBasket_Marquise_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral Pavé + Marquise + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Marquise'] && nodes['2_PaveHalo_Marquise'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Marquise'].geometry.clone()} key={'2_PaveHalo_Marquise_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral Pavé + Marquise + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 6 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Marquise_Diamonds'] && nodes['2_PaveHalo_Marquise_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Marquise_Diamonds'].geometry.clone()} key={'2_PaveHalo_Marquise_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral Pavé + Asscher + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Asscher'] && nodes['2_ClassicBasket_Asscher'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Asscher'].geometry.clone()} key={'2_ClassicBasket_Asscher_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral Pavé + Asscher + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Asscher_Diamonds'] && nodes['2_ClassicBasket_Asscher_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Asscher_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Asscher_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral Pavé + Asscher + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Asscher'] && nodes['2_4P_Asscher'].geometry && (
            <mesh geometry={nodes['2_4P_Asscher'].geometry.clone()} key={'2_4P_Asscher_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral Pavé + Asscher + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Asscher'] && nodes['2_PaveBasket_Asscher'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Asscher'].geometry.clone()} key={'2_PaveBasket_Asscher_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral Pavé + Asscher + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Asscher_Diamonds'] && nodes['2_PaveBasket_Asscher_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Asscher_Diamonds'].geometry.clone()} key={'2_PaveBasket_Asscher_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral Pavé + Asscher + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Asscher'] && nodes['2_PaveHalo_Asscher'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Asscher'].geometry.clone()} key={'2_PaveHalo_Asscher_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral Pavé + Asscher + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 7 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Asscher_Diamonds'] && nodes['2_PaveHalo_Asscher_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Asscher_Diamonds'].geometry.clone()} key={'2_PaveHalo_Asscher_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral Pavé + Radiant + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Radiant'] && nodes['2_ClassicBasket_Radiant'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Radiant'].geometry.clone()} key={'2_ClassicBasket_Radiant_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral Pavé + Radiant + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Radiant_Diamonds'] && nodes['2_ClassicBasket_Radiant_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Radiant_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Radiant_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* 4-Prong mesh for Cathedral Pavé + Radiant + Classic Basket annotation (additional to Classic Basket) */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_4P_Radiant'] && nodes['2_4P_Radiant'].geometry && (
            <mesh geometry={nodes['2_4P_Radiant'].geometry.clone()} key={'2_4P_Radiant_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral Pavé + Radiant + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Radiant'] && nodes['2_PaveBasket_Radiant'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Radiant'].geometry.clone()} key={'2_PaveBasket_Radiant_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral Pavé + Radiant + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Radiant_Diamonds'] && nodes['2_PaveBasket_Radiant_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Radiant_Diamonds'].geometry.clone()} key={'2_PaveBasket_Radiant_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral Pavé + Radiant + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Radiant'] && nodes['2_PaveHalo_Radiant'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Radiant'].geometry.clone()} key={'2_PaveHalo_Radiant_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral Pavé + Radiant + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 8 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Radiant_Diamonds'] && nodes['2_PaveHalo_Radiant_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Radiant_Diamonds'].geometry.clone()} key={'2_PaveHalo_Radiant_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket mesh for Cathedral Pavé + Heart + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Heart'] && nodes['2_ClassicBasket_Heart'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Heart'].geometry.clone()} key={'2_ClassicBasket_Heart_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Classic Basket Diamonds mesh for Cathedral Pavé + Heart + Classic Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'classic-basket' && nodes['2_ClassicBasket_Heart_Diamonds'] && nodes['2_ClassicBasket_Heart_Diamonds'].geometry && (
            <mesh geometry={nodes['2_ClassicBasket_Heart_Diamonds'].geometry.clone()} key={'2_ClassicBasket_Heart_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket mesh for Cathedral Pavé + Heart + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Heart'] && nodes['2_PaveBasket_Heart'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Heart'].geometry.clone()} key={'2_PaveBasket_Heart_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Basket Diamonds mesh for Cathedral Pavé + Heart + Pavé Basket annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-basket' && nodes['2_PaveBasket_Heart_Diamonds'] && nodes['2_PaveBasket_Heart_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveBasket_Heart_Diamonds'].geometry.clone()} key={'2_PaveBasket_Heart_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo mesh for Cathedral Pavé + Heart + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Heart'] && nodes['2_PaveHalo_Heart'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Heart'].geometry.clone()} key={'2_PaveHalo_Heart_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]}>
              <meshPhysicalMaterial
                color={metals && typeof selectedSettingMetal === 'number' ? metals[selectedSettingMetal] : '#E5E4E2'}
                roughness={0.1}
                metalness={0.9}
                clearcoat={1.0}
                clearcoatRoughness={0.1}
                transmission={0.0}
                ior={1.5}
                thickness={0.5}
                envMap={metalEnv}
                envMapIntensity={0.8}
                clipShadows
              />
            </mesh>
          )}
          
          {/* Pavé Halo Diamonds mesh for Cathedral Pavé + Heart + Pavé Halo annotation */}
          {!showOnlyDiamond && selectedSettingStyle === 9 && selectedBandStyle === 0 && selectedDiamondShape === 9 && selectedCathedralAnnotation === 'pave-halo' && nodes['2_PaveHalo_Heart_Diamonds'] && nodes['2_PaveHalo_Heart_Diamonds'].geometry && (
            <mesh geometry={nodes['2_PaveHalo_Heart_Diamonds'].geometry.clone()} key={'2_PaveHalo_Heart_Diamonds_CathedralPave'} castShadow receiveShadow position={[0, 0, 0]} layers={1}>
              <MeshRefractionMaterial
                envMap={diamondEnv}
                bounces={diamondProps.bounces}
                ior={diamondProps.ior}
                fresnel={diamondProps.fresnel}
                aberrationStrength={diamondProps.aberrationStrength}
                color={diamondProps.color}
                side={THREE.DoubleSide}
                toneMapped={true}
                envMapIntensity={1.5}
                {...(clippingPlane ? {clippingPlanes: [clippingPlane]} : {})}
                clipShadows
              />
            </mesh>
          )}
        </group>
      </group>
    );
}

// Temporary component to log all node names in Andromeda.glb
function LogAndromedaNodes() {
  const { nodes } = useGLTF('/Andromeda.glb');
  useEffect(() => {
    console.log('Andromeda.glb node names:', Object.keys(nodes));
  }, [nodes]);
  return null;
}

// Loading Bar Component
function LoadingBar() {
  const { progress, active } = useProgress();
  
  // Cycling loading messages
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const loadingMessages = [
    'Initializing 3D engine...',
    'Loading ring models...',
    'Preparing materials...',
    'Rendering scene...',
    'Optimizing lighting...',
    'Finalizing configuration...'
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex(prev => (prev + 1) % loadingMessages.length);
    }, 1500); // Change message every 1.5 seconds
    
    return () => clearInterval(interval);
  }, [loadingMessages.length]);
  
  // Show final message when loading is complete
  const displayMessage = (progress >= 100 && !active) 
    ? 'Finalizing configuration...' 
    : loadingMessages[currentMessageIndex];
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      maxWidth: '400px',
      padding: '40px',
      textAlign: 'center'
    }}>
        {/* Animated Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%)',
          animation: 'pulse 4s ease-in-out infinite alternate'
        }} />
        
        {/* Main Content Container */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          maxWidth: '400px',
          padding: '40px'
        }}>
          {/* Logo/Icon */}
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 30px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            animation: 'float 3s ease-in-out infinite'
          }}>
            <div style={{
              fontSize: '32px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: '300'
            }}>
              💎
            </div>
        </div>
        
          {/* Title */}
        <div style={{
            fontSize: '28px',
            fontWeight: '600',
            color: 'rgba(255, 255, 255, 0.95)',
            marginBottom: '8px',
            letterSpacing: '-0.5px'
          }}>
            Ring Configurator
          </div>
          
          {/* Subtitle */}
          <div style={{
            fontSize: '16px',
            fontWeight: '400',
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '40px',
            letterSpacing: '0.2px'
          }}>
            Loading your perfect ring...
          </div>
          
                    {/* Loading Message */}
          <div style={{
            fontSize: '18px',
            fontWeight: '500',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '30px',
            minHeight: '27px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 0.3s ease'
          }}>
            {displayMessage}
          </div>
        </div>
        
        {/* CSS Animations */}
        <style>{`
          @keyframes pulse {
            0% { opacity: 0.6; }
            100% { opacity: 1; }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          
          @keyframes shimmer {
            0% { left: -100%; }
            100% { left: 100%; }
          }
        `}</style>
      </div>
  );
}

// --- Sidebar UI for Ring Customizer ---
function RingCustomizerSidebar({ selectedDiamondShape, setSelectedDiamondShape, selectedSettingStyle, setSelectedSettingStyle, selectedBandStyle, setSelectedBandStyle, selectedSettingMetal, setSelectedSettingMetal, selectedBandMetal, setSelectedBandMetal, metals, metalNames, selectedDiamondSize, setSelectedDiamondSize, diamondSizeData, settingStyles, bandStyles }) {
  const diamondShapeNodeNames = [
    "1_Round",      // 0
    "1_Princess",   // 1
    "1_Oval",       // 2
    "1_Emerald",    // 3
    "1_Pear",       // 4
    "1_Cushion",    // 5
    "1_Marquise",   // 6
    "1_Asscher",    // 7
    "1_Radiant",    // 8
    "1_Heart"       // 9
  ];
  const selectedNode = diamondShapeNodeNames[selectedDiamondShape];

  const diamondShapes = [
    { label: 'Round', icon: <img src="/icons/Diamond Types/Round.png" alt="Round" style={{width: 28, height: 28}} /> },
    { label: 'Princess', icon: <img src="/icons/Diamond Types/Princess.png" alt="Princess" style={{width: 28, height: 28}} /> },
    { label: 'Oval', icon: <img src="/icons/Diamond Types/Oval.png" alt="Oval" style={{width: 28, height: 28}} /> },
    { label: 'Emerald', icon: <img src="/icons/Diamond Types/Emerald.png" alt="Emerald" style={{width: 28, height: 28}} /> },
    { label: 'Pear', icon: <img src="/icons/Diamond Types/Pear.png" alt="Pear" style={{width: 28, height: 28}} /> },
    { label: 'Cushion', icon: <img src="/icons/Diamond Types/Cushion.png" alt="Cushion" style={{width: 28, height: 28}} /> },
    { label: 'Marquise', icon: <img src="/icons/Diamond Types/Marquise.png" alt="Marquise" style={{width: 28, height: 28}} /> },
    { label: 'Asscher', icon: <img src="/icons/Diamond Types/Asscher.png" alt="Asscher" style={{width: 28, height: 28}} /> },
    { label: 'Radiant', icon: <img src="/icons/Diamond Types/Radiant.png" alt="Radiant" style={{width: 28, height: 28}} /> },
    { label: 'Heart', icon: <img src="/icons/Diamond Types/Heart.png" alt="Heart" style={{width: 28, height: 28}} /> },
  ];


  React.useEffect(() => {
    console.log('Selected Diamond Shape Node:', diamondShapeNodeNames[selectedDiamondShape]);
  }, [selectedDiamondShape]);

  return (
    <aside style={{
      background: '#fff',
      borderLeft: '2px solid #eee',
      padding: '32px 24px 24px 24px',
      overflowY: 'auto',
      boxShadow: '-2px 0 16px rgba(0,0,0,0.04)',
      fontFamily: 'Inter, sans-serif',
    }}>
      <section>
        <div style={{ fontWeight: 400, fontSize: 16, marginBottom: 8, paddingLeft: 2 }}>Diamond Shapes</div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 10, paddingLeft: 2
        }}>
          {diamondShapes.map((shape, i) => {
            // Pavé Halo logic: only Round (i === 0) is enabled if selectedSettingStyle === 3
            const paveHaloActive = selectedSettingStyle === 3;
            // Four Prongs logic: only indices 0,1,2,3,4,5,7,8 are enabled if selectedSettingStyle === 0
            const fourProngsActive = selectedSettingStyle === 0;
            const fourProngsEnabledIndices = [0, 1, 2, 3, 5, 7, 8];
            // Six Prongs logic: only indices 0 (Round) and 6 (Marquise) are enabled if selectedSettingStyle === 1
            const sixProngsActive = selectedSettingStyle === 1;
            const sixProngsEnabledIndices = [0, 6];
            // Classic Basket logic: only indices 0-9 are enabled if selectedSettingStyle === 2
            const classicBasketActive = selectedSettingStyle === 2;
            const classicBasketEnabledIndices = [0,1,2,3,4,5,6,7,8,9];
            // Pavé Basket logic: only Asscher (i === 7) is enabled if selectedSettingStyle === 4
            const paveBasketActive = selectedSettingStyle === 4;
            // 4-P Bloom logic: all shapes except Heart enabled if selectedSettingStyle === 5
            const fourPBloomActive = selectedSettingStyle === 5;
            const fourPBloomEnabledIndices = [0,1,2,3,5,7,8];
            // 6-P Bloom logic: only Round and Marquise enabled if selectedSettingStyle === 6
            const sixPBloomActive = selectedSettingStyle === 6;
            const sixPBloomEnabledIndices = [0, 6];
            let isEnabled = true;
            if (sixPBloomActive) {
              isEnabled = sixPBloomEnabledIndices.includes(i);
            } else if (paveHaloActive) {
              isEnabled = i === 0 || i === 1 || i === 2 || i === 3 || i === 4 || i === 5 || i === 6 || i === 7 || i === 8;
            } else if (paveBasketActive) {
              isEnabled = i >= 0 && i <= 9;
            } else if (fourProngsActive) {
              isEnabled = fourProngsEnabledIndices.includes(i);
            } else if (sixProngsActive) {
              isEnabled = sixProngsEnabledIndices.includes(i);
            } else if (classicBasketActive) {
              isEnabled = classicBasketEnabledIndices.includes(i);
            } else if (fourPBloomActive) {
              isEnabled = fourPBloomEnabledIndices.includes(i);
            }
            return (
              <button
                key={shape.label}
                title={shape.label}
                onClick={isEnabled ? () => setSelectedDiamondShape(i) : undefined}
                style={{
                  width: 44,
                  height: 44,
                  border: selectedDiamondShape === i ? '1.5px solid #111' : 'none',
                  borderRadius: i < 10 ? 0 : 8,
                  background: '#fafafa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isEnabled ? 'pointer' : 'not-allowed',
                  fontSize: 24,
                  padding: 0,
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  opacity: isEnabled ? 1 : 0.3,
                  pointerEvents: isEnabled ? 'auto' : 'none',
                }}
              >
                {i < 10 ? (
                  <img src={
                                    i === 0 ? "/icons/Diamond Types/Round.png" :
                i === 1 ? "/icons/Diamond Types/Princess.png" :
                i === 2 ? "/icons/Diamond Types/Oval.png" :
                i === 3 ? "/icons/Diamond Types/Emerald.png" :
                i === 4 ? "/icons/Diamond Types/Pear.png" :
                i === 5 ? "/icons/Diamond Types/Cushion.png" :
                i === 6 ? "/icons/Diamond Types/Marquise.png" :
                i === 7 ? "/icons/Diamond Types/Asscher.png" :
                i === 8 ? "/icons/Diamond Types/Radiant.png" :
                "/icons/Diamond Types/Heart.png"
                  } alt={shape.label} style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}} />
                ) : (
                  shape.icon
                )}
              </button>
            );
          })}
        </div>
        {/* Diamond Size Heading and Options */}
        <div style={{ fontWeight: 100, fontSize: 12, margin: '20px 10px 8px 0', color: '#111', width: 'fit-content', cursor: 'pointer' }}>
          Diamond Size
        </div>
        <div style={{ display: 'flex', gap: 18, marginBottom: 18, marginTop: 8 }}>
          {diamondSizeData.map((sizeData, i) => (
            <button
              key={sizeData.label}
              onClick={() => setSelectedDiamondSize(i)}
              style={{
                background: 'none',
                border: selectedDiamondSize === i ? '1px solid #111' : 'none',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 12,
                color: selectedDiamondSize === i ? '#111' : '#666',
                cursor: 'pointer',
                fontWeight: selectedDiamondSize === i ? 600 : 400,
                minWidth: 0,
                width: 'auto',
                transition: 'all 0.2s ease',
              }}
            >
              {sizeData.label}
            </button>
          ))}
        </div>
      </section>
      {/* Bi-Metal section removed */}
      <section>
        <div style={{ fontWeight: 400, fontSize: 16, margin: '18px 0 10px 0', paddingLeft: 2 }}>Setting Styles</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, margin: '18px 0 10px -8px', paddingLeft: 2 }}>
          {settingStyles.map((style, i) => (
            <button
              key={style.label}
              onClick={() => setSelectedSettingStyle(i)}
              style={{
                minWidth: 0,
                width: 64,
                height: 74,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'center',
                padding: 0,
                boxSizing: 'border-box',
              }}
            >
              <div style={{ 
                width: 44, 
                height: 44, 
                border: selectedSettingStyle === i ? '1.5px solid #111' : '1.5px solid #bbb', 
                borderRadius: 4, 
                background: '#fafafa', 
                marginBottom: 2, 
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                <img 
                  src={style.icon} 
                  alt={style.label} 
                  style={{
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    display: 'block'
                  }} 
                />
              </div>
              <span style={{ fontSize: 12, marginTop: 2, lineHeight: 1.1 }}>{style.label}</span>
            </button>
          ))}
        </div>
        {
          <>
            <div style={{ fontWeight: 100, fontSize: 12, margin: '18px 0 10px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Setting Metal</span>
              <span style={{ fontSize: 11, color: '#666', fontWeight: 400 }}>{metalNames[selectedSettingMetal]}</span>
            </div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
          {metals.map((color, i) => (
            <button
              key={i}
              onClick={() => setSelectedSettingMetal(i)}
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                border: selectedSettingMetal === i ? '1.5px solid #111' : '1.5px solid #bbb',
                marginRight: 0,
                marginBottom: 10,
                cursor: 'pointer',
                background: color,
                boxSizing: 'border-box',
              }}
            />
          ))}
        </div>
          </>
        }
      </section>
      <section>
        <div style={{ fontWeight: 400, fontSize: 16, margin: '18px 0 20px 0', paddingLeft: 2 }}>Band Styles</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, margin: '18px 0 10px -8px', paddingLeft: 2 }}>
          {bandStyles.map((style, i) => {
            // Four Prongs, Six Prongs, Classic Basket, Pavé Halo, Pavé Basket, 4-P Bloom, or 6-P Bloom logic: only indices 0,1,2,3 are enabled if selectedSettingStyle === 0, 1, 2, 3, 4, 5, or 6
            const prongsOrClassicOrHaloOrBasketOrBloomActive = selectedSettingStyle === 0 || selectedSettingStyle === 1 || selectedSettingStyle === 2 || selectedSettingStyle === 3 || selectedSettingStyle === 4 || selectedSettingStyle === 5 || selectedSettingStyle === 6;
            const prongsEnabledIndices = [0, 1, 2, 3];
            // Pear 3 Stone logic: only index 0 is enabled if selectedSettingStyle === 7
            const pear3StoneActive = selectedSettingStyle === 7;
            const pear3StoneEnabledIndices = [0];
            // Cathedral logic: only Solitaire (index 0) is enabled if selectedSettingStyle === 8
            const cathedralActive = selectedSettingStyle === 8;
            const cathedralEnabledIndices = [0];
            // Cathedral Pavé logic: only Solitaire (index 0) is enabled if selectedSettingStyle === 9
            const cathedralPaveActive = selectedSettingStyle === 9;
            const cathedralPaveEnabledIndices = [0];
            let isEnabled = true;
            if (prongsOrClassicOrHaloOrBasketOrBloomActive) {
              isEnabled = prongsEnabledIndices.includes(i);
            } else if (pear3StoneActive) {
              isEnabled = pear3StoneEnabledIndices.includes(i);
            } else if (cathedralActive) {
              isEnabled = cathedralEnabledIndices.includes(i);
            } else if (cathedralPaveActive) {
              isEnabled = cathedralPaveEnabledIndices.includes(i);
            }
            return (
            <button
              key={style.label}
                onClick={isEnabled ? () => {
                console.log('Band style selected:', i, style.label);
                setSelectedBandStyle(i);
                } : undefined}
              style={{
                minWidth: 0,
                width: 64,
                height: 74,
                border: 'none',
                background: 'none',
                  cursor: isEnabled ? 'pointer' : 'not-allowed',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'center',
                padding: 0,
                boxSizing: 'border-box',
                  opacity: isEnabled ? 1 : 0.3,
                  pointerEvents: isEnabled ? 'auto' : 'none',
              }}
            >
              <div style={{ 
                width: 44, 
                height: 44, 
                border: selectedBandStyle === i ? '1.5px solid #111' : '1.5px solid #bbb', 
                borderRadius: 4, 
                background: '#fafafa', 
                marginBottom: 2, 
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                <img 
                  src={style.icon} 
                  alt={style.label} 
                  style={{
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    display: 'block'
                  }} 
                />
              </div>
              <span style={{ fontSize: 12, marginTop: 2, lineHeight: 1.1 }}>{style.label}</span>
            </button>
            );
          })}
        </div>
        {
          <>
            <div style={{ fontWeight: 100, fontSize: 12, margin: '18px 0 10px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Band Metal</span>
              <span style={{ fontSize: 11, color: '#666', fontWeight: 400 }}>{metalNames[selectedBandMetal]}</span>
            </div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
          {metals.map((color, i) => (
            <button
              key={i}
              onClick={() => setSelectedBandMetal(i)}
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                border: selectedBandMetal === i ? '1.5px solid #111' : '1.5px solid #bbb',
                marginRight: 0,
                marginBottom: 10,
                cursor: 'pointer',
                background: color,
                boxSizing: 'border-box',
              }}
            />
          ))}
        </div>
          </>
        }
      </section>
    </aside>
  );
}